import { useState, useEffect, useCallback, useRef, Fragment } from 'react'
import { useNavigate } from 'react-router-dom'
import { normaliseProductSlug, isValidProductSlug, PRODUCT_SLUG_RULE } from '../utils/productSlug'
import '@google/model-viewer'
import OsteoLogo from '../components/Logo'
import ErrorBoundary from '../components/ErrorBoundary'
import TrackerTab from '../components/TrackerTab'
import SseUpdatesPanel from '../components/SseUpdatesPanel'
import PodiatryFormPanel from '../components/PodiatryFormPanel'
import api, {
  adminLogout,
  getMetrics,
  getQueueStats,
  retryFailedJobs,
  getCases,
  createCase,
  updateCase,
  toggleCaseStatus,
  toggleCaseDone,
  deleteCase,
  deleteCases,
  getUploadsForCase,
  validateUpload,
  deleteUploadSession,
  getRecipients,
  addRecipient,
  updateRecipient,
  removeRecipient,
  deleteRecipientPermanent,
  sendTestMessage,
  shareCase,
  shareAllContacts,
  regeneratePin,
  getCranialCases,
  createCranialCase,
  regenerateCranialPin,
  reinitiateCranialCase,
  setCranialCaseActive,
  deleteCranialCase,
  setPaymentAmount,
  waivePayment,
  getPaymentHistory,
  getOrphanUploads,
  getDicomQuality,
  getCaseDicomMeta,
  purgeAllOrphans,
  deleteOrphan,
  getCaseContacts,
  addCaseContact,
  deleteCaseContact,
  getModelsForCase,
  uploadModel,
  deleteModel,
  getAnnotations,
  updateAnnotationStatus,
  requestReview,
  getCaseAnnotations,
  getAnnotationFileUrl,
  getAdminUsers,
  addAdminUser,
  removeAdminUser,
  getDriveStatus,
  cleanQueue,
  getLicenseProducts,
  createLicenseProduct,
  getLicenses,
  issueLicense,
  getLicenseDetail,
  updateLicense,
  revokeLicense,
  renewLicense,
  getLicenseStats,
  deleteLicense,
  bulkPurgeLicenses,
  createMeeting,
  getAllMeetings,
  rescheduleMeeting,
  cancelMeeting,
  deleteMeeting,
  bulkDeleteMeetings,
  getDriveConnectionStatus,
  getCalendarStatus,
  scGetDoctors,
  scVerifyDoctor,
  scDeleteDoctor,
  scGetStats,
  getSseReleases,
  setSseWorkstation,
} from '../services/api'

// ── Helpers ───────────────────────────────────────────────────────
function formatBytes(bytes) {
  const b = Number(bytes)
  if (!b) return '0 B'
  const k = 1024
  const sizes = ['B', 'KB', 'MB', 'GB']
  const i = Math.floor(Math.log(b) / Math.log(k))
  return `${parseFloat((b / Math.pow(k, i)).toFixed(1))} ${sizes[i]}`
}

function formatDate(dateStr) {
  return new Date(dateStr).toLocaleDateString('en-GB', {
    day: '2-digit', month: 'short', year: 'numeric',
  })
}

const VAL_PILL = {
  PASSED:  'bg-green-100 text-green-700',
  WARNING: 'bg-yellow-100 text-yellow-700',
  FAILED:  'bg-red-100 text-red-600',
  PENDING: 'bg-gray-100 text-gray-500',
}

const PAY_PILL = {
  NONE:    'bg-gray-100 text-gray-400',
  PENDING: 'bg-orange-100 text-orange-700',
  PAID:    'bg-green-100 text-green-700',
  WAIVED:  'bg-blue-100 text-blue-600',
}


// ── Root component ────────────────────────────────────────────────
export default function AdminDashboard() {
  const navigate              = useNavigate()
  const [email, setEmail]     = useState(null)
  const [role, setRole]       = useState(null)
  const [loggingOut, setOut]  = useState(false)
  const [tab, setTab]         = useState('overview')
  const [scPendingCount, setScPendingCount] = useState(0)

  useEffect(() => {
    api.get('/admin/auth/me')
      .then((res) => { setEmail(res.data.email); setRole(res.data.role) })
      .catch(() => {})
  }, [])

  useEffect(() => {
    scGetStats().then(r => setScPendingCount(r.data.pendingCount ?? 0)).catch(() => {})
  }, [])

  useEffect(() => {
    const err = new URLSearchParams(window.location.search).get('error')
    if (err === 'wrong_calendar_account') {
      window.alert('The planning calendar can only be connected by contactus@osteo3d.com. Sign out, sign in as that account, then retry Connect Calendar.')
      window.history.replaceState({}, '', window.location.pathname)
    }
  }, [])

  async function handleLogout() {
    setOut(true)
    try { await adminLogout() } finally {
      sessionStorage.removeItem('adminToken')
      navigate('/admin/login', { replace: true })
    }
  }

  const isAdmin = role === 'admin'
  const allTabs = [
    { id: 'overview',   label: 'Overview'    },
    { id: 'cases',      label: 'Cases'       },
    { id: 'meet',       label: 'Meet'        },
    { id: 'markings',   label: 'Markings'    },
    { id: 'recipients', label: 'Recipients'  },
    { id: 'orphans',    label: 'Orphans'     },
    { id: 'users',         label: 'Users',       adminOnly: true },
    { id: 'licenses',      label: 'Licenses'    },
    { id: 'surg-connect',  label: 'SurgConnect', adminOnly: true },
    { id: 'forma', label: 'Forma' },
    // Tracker re-enabled 2026-07-10 after the DB load was fixed: sync is now
    // manual-only and the mirror keeps only active cases (NEW/IN_PRODUCTION/
    // ON_HOLD, ~1,691 rows / ~3 MB, was ~23 MB). See memory: dicom-portal-missing-indexes.md.
    { id: 'tracker', label: 'Tracker' },
  ]
  const tabs = allTabs.filter(t => !t.adminOnly || isAdmin)

  return (
    <div className="min-h-screen bg-gray-50">

      {/* Header */}
      <header className="bg-white border-b border-gray-200 px-6 py-3 flex items-center justify-between">
        <div className="flex items-center gap-4">
          <OsteoLogo size="sm" />
          <div className="border-l border-gray-200 pl-4">
            <p className="text-sm font-semibold text-gray-700 leading-none">Admin Portal</p>
            <p className="text-xs text-gray-400 mt-0.5">Medical Data Upload Portal</p>
          </div>
        </div>
        <div className="flex items-center gap-4">
          {email && <span className="text-sm text-gray-600 hidden sm:block">{email}</span>}
          <button
            onClick={handleLogout}
            disabled={loggingOut}
            className="btn-secondary text-sm px-4 py-2"
          >
            {loggingOut ? 'Signing out…' : 'Sign out'}
          </button>
        </div>
      </header>

      {/* Tab nav */}
      <div className="bg-white border-b border-gray-200 px-6">
        <nav className="max-w-7xl mx-auto flex -mb-px">

          {/* Management tabs */}
          {tabs.map((t) => (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              className={[
                'px-5 py-3.5 text-sm font-medium border-b-2 transition-colors flex items-center gap-1.5',
                tab === t.id
                  ? 'border-brand-600 text-brand-700'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300',
              ].join(' ')}
            >
              {t.label}
              {t.id === 'surg-connect' && scPendingCount > 0 && (
                <span className="bg-red-500 text-white text-xs font-bold px-1.5 py-0.5 rounded-full leading-none">
                  {scPendingCount}
                </span>
              )}
            </button>
          ))}


        </nav>
      </div>

      <main className="max-w-7xl mx-auto px-4 py-6">
        {/* One tab's render error must not take the portal down with it — this
            page also carries the customer DICOM upload flow. Keyed by tab so
            switching away from a broken tab clears the error. */}
        <ErrorBoundary key={tab} label={`The ${tab} tab`}>
        {tab === 'overview'   && <OverviewTab role={role} />}
        {tab === 'cases'      && <CasesTab role={role} />}
        {tab === 'meet'       && <MeetTab role={role} />}
        {tab === 'markings'   && <MarkingsTab role={role} />}
        {tab === 'recipients' && <RecipientsTab role={role} />}
        {tab === 'orphans'    && <OrphansTab role={role} />}
        {tab === 'users'      && <UsersTab />}
        {tab === 'licenses'   && <LicensesTab role={role} />}
        {tab === 'surg-connect' && <SurgConnectTab onPendingChange={setScPendingCount} />}
        {tab === 'forma' && <FormaTab role={role} />}
        {tab === 'tracker' && <TrackerTab />}
        </ErrorBoundary>
      </main>

    </div>
  )
}

// ═════════════════════════════════════════════════════════════════
// Overview tab
// ═════════════════════════════════════════════════════════════════
function OverviewTab({ role }) {
  const isAdmin = role === 'admin'
  const [metrics,   setMetrics]   = useState(null)
  const [queue,     setQueue]     = useState(null)
  const [loading,   setLoading]   = useState(true)
  const [retrying,  setRetrying]  = useState(false)
  const [retryMsg,  setRetryMsg]  = useState(null)
  const [driveOk,   setDriveOk]   = useState(null) // null=loading, true/false/string
  const [driveEmail, setDriveEmail] = useState(null)
  const [cleaning,  setCleaning]  = useState(false)

  function loadAll() {
    setLoading(true)
    // Load metrics first (fast, DB only) — don't block on queue stats (Redis)
    getMetrics()
      .then((mRes) => setMetrics(mRes.data))
      .catch(() => {})
      .finally(() => setLoading(false))
    // Queue stats loaded independently — won't block page render
    getQueueStats()
      .then((qRes) => setQueue(qRes.data))
      .catch(() => setQueue({ waiting: 0, active: 0, completed: 0, failed: 0, delayed: 0, unavailable: true }))
    // Drive health check
    getDriveStatus()
      .then((r) => setDriveOk(r.data.ok ? true : r.data.error))
      .catch(() => setDriveOk('Unable to check'))
    // Drive connection info (which account owns Drive)
    getDriveConnectionStatus()
      .then((r) => setDriveEmail(r.data.email))
      .catch(() => {})
  }

  async function handleClean() {
    setCleaning(true)
    try {
      await cleanQueue()
      getQueueStats().then((r) => setQueue(r.data)).catch(() => {})
    } catch {}
    setCleaning(false)
  }

  useEffect(loadAll, []) // eslint-disable-line react-hooks/exhaustive-deps

  // Metrics use stale-while-revalidate: the first response may be a warming
  // snapshot (stale/null) while the server refreshes in the background. Re-fetch
  // shortly after so the real numbers appear without a manual reload.
  useEffect(() => {
    if (metrics && (metrics.stale || metrics.cases?.total === null)) {
      const t = setTimeout(() => {
        getMetrics().then((r) => setMetrics(r.data)).catch(() => {})
      }, 3000)
      return () => clearTimeout(t)
    }
  }, [metrics])

  async function handleRetry() {
    setRetrying(true)
    setRetryMsg(null)
    try {
      const res = await retryFailedJobs()
      setRetryMsg(`Retried ${res.data.retried} job${res.data.retried !== 1 ? 's' : ''}.`)
      // Refresh queue counts after retry
      getQueueStats().then((r) => setQueue(r.data)).catch(() => {})
    } catch {
      setRetryMsg('Retry failed — check server logs.')
    } finally {
      setRetrying(false)
      setTimeout(() => setRetryMsg(null), 4000)
    }
  }

  if (loading) return <CentredSpinner />
  if (!metrics) {
    return <p className="text-gray-400 text-sm text-center mt-8">Could not load metrics.</p>
  }

  // Metrics may arrive with null fields when the snapshot is still warming up
  // (stale-while-revalidate) — render those as an em dash instead of "null".
  const dash = (v) => (v === null || v === undefined ? '—' : v)
  const statCards = [
    {
      label:  'Total Cases',
      value:  dash(metrics.cases.total),
      sub:    `${dash(metrics.cases.active)} active`,
      colour: 'text-brand-700',
    },
    {
      label:  'Total Uploads',
      value:  dash(metrics.uploads.total),
      sub:    `${dash(metrics.uploads.last24h)} in last 24 h`,
      colour: 'text-gray-700',
    },
    {
      label:  'Failed Validations',
      value:  dash(metrics.uploads.failedValidation),
      sub:    'require review',
      colour: metrics.uploads.failedValidation > 0 ? 'text-red-600' : 'text-gray-700',
    },
  ]

  const queueItems = queue ? [
    { label: 'Waiting',   value: queue.waiting,   colour: 'text-blue-600'  },
    { label: 'Active',    value: queue.active,     colour: 'text-yellow-600' },
    { label: 'Completed', value: queue.completed,  colour: 'text-green-600' },
    { label: 'Failed',    value: queue.failed,     colour: queue.failed > 0 ? 'text-red-600' : 'text-gray-400' },
    { label: 'Delayed',   value: queue.delayed,    colour: 'text-gray-500'  },
  ] : []

  return (
    <div className="space-y-5">

      {/* Drive status */}
      <div className={`rounded-xl border p-4 flex items-center gap-3 ${
        driveOk === null ? 'bg-gray-50 border-gray-200' :
        driveOk === true ? 'bg-green-50 border-green-200' :
        'bg-red-50 border-red-200'
      }`}>
        <span className={`inline-block w-3 h-3 rounded-full ${
          driveOk === null ? 'bg-gray-300 animate-pulse' :
          driveOk === true ? 'bg-green-500' : 'bg-red-500'
        }`} />
        <div>
          <p className={`text-sm font-semibold ${
            driveOk === null ? 'text-gray-500' :
            driveOk === true ? 'text-green-700' : 'text-red-700'
          }`}>
            {driveOk === null ? 'Checking Google Drive…' :
             driveOk === true ? 'Google Drive connected' :
             'Google Drive disconnected'}
          </p>
          {driveEmail && driveOk === true && (
            <p className="text-xs text-green-600 mt-0.5">Account: {driveEmail}</p>
          )}
          {driveOk !== null && driveOk !== true && (
            <p className="text-xs text-red-600 mt-0.5">{typeof driveOk === 'string' ? driveOk : 'Token expired'} — uploads will fail.</p>
          )}
        </div>
        {isAdmin && driveOk !== true && (
          <a
            href={`${import.meta.env.VITE_API_BASE_URL || ''}/api/admin/auth/drive?redirect=${encodeURIComponent(window.location.href)}`}
            className="ml-auto text-xs px-3 py-1.5 rounded-lg font-medium whitespace-nowrap transition-colors bg-red-600 text-white hover:bg-red-700"
          >
            Connect Drive
          </a>
        )}
      </div>

      {/* Stats row */}
      <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
        {statCards.map((c) => (
          <div key={c.label} className="bg-white rounded-xl border border-gray-200 p-5">
            <p className="text-xs text-gray-400 mb-1">{c.label}</p>
            <p className={`text-3xl font-bold ${c.colour}`}>{c.value}</p>
            <p className="text-xs text-gray-400 mt-1">{c.sub}</p>
          </div>
        ))}
      </div>

      {/* Notification queue card */}
      {queue && (
        <div className="bg-white rounded-xl border border-gray-200 p-5">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h3 className="text-sm font-semibold text-gray-700">Notification Queue</h3>
              <p className="text-xs text-gray-400 mt-0.5">BullMQ · Redis-backed</p>
            </div>
            <div className="flex items-center gap-3">
              {retryMsg && (
                <span className="text-xs text-gray-500">{retryMsg}</span>
              )}
              {isAdmin && (queue.completed > 0 || queue.failed > 0) && (
                <button
                  onClick={handleClean}
                  disabled={cleaning}
                  className="text-xs px-3 py-1.5 rounded-lg border border-gray-200 text-gray-500 hover:bg-gray-50 transition-colors disabled:opacity-50"
                >
                  {cleaning ? 'Cleaning…' : 'Clear history'}
                </button>
              )}
              {isAdmin && queue.failed > 0 && (
                <button
                  onClick={handleRetry}
                  disabled={retrying}
                  className="text-xs px-3 py-1.5 rounded-lg border border-red-200 bg-red-50 text-red-600 hover:bg-red-100 transition-colors disabled:opacity-50"
                >
                  {retrying ? 'Retrying…' : `Retry ${queue.failed} failed`}
                </button>
              )}
              <button
                onClick={loadAll}
                className="text-xs px-3 py-1.5 rounded-lg border border-gray-200 text-gray-500 hover:bg-gray-50 transition-colors"
              >
                Refresh
              </button>
            </div>
          </div>

          <div className="flex items-center gap-6 flex-wrap">
            {queueItems.map((item) => (
              <div key={item.label} className="text-center min-w-[56px]">
                <p className={`text-2xl font-bold ${item.colour}`}>{item.value}</p>
                <p className="text-[11px] text-gray-400 mt-0.5">{item.label}</p>
              </div>
            ))}
          </div>
        </div>
      )}

    </div>
  )
}

// ═════════════════════════════════════════════════════════════════
// Cases tab
// ═════════════════════════════════════════════════════════════════
function CasesTab({ role }) {
  const isAdmin = role === 'admin'
  const [cases,        setCases]        = useState([])
  const [total,        setTotal]        = useState(0)
  const [page,         setPage]         = useState(1)
  const [pages,        setPages]        = useState(1)
  const [search,       setSearch]       = useState('')
  const [loading,      setLoading]      = useState(true)
  const [showCreate,   setShowCreate]   = useState(false)
  const [creating,     setCreating]     = useState(false)
  const [newNotes,     setNewNotes]     = useState('')
  const [newCaseId,    setNewCaseId]    = useState('')
  const [createErr,    setCreateErr]    = useState(null)
  const [editModal,    setEditModal]    = useState(null)   // null | { caseId, caseIdVal, notesVal }
  const [editSaving,   setEditSaving]   = useState(false)
  const [editErr,      setEditErr]      = useState(null)
  const [expandedId,        setExpandedId]        = useState(null)
  const [uploadsMap,        setUploadsMap]        = useState({})
  const [uLoading,          setULoading]          = useState(null)
  const [validatingUploadId, setValidatingUploadId] = useState(null)
  const [deletingSession,    setDeletingSession]    = useState(null) // sessionFolder or uploadId being deleted
  const [toggling,     setToggling]     = useState(null)
  const [togglingDone, setTogglingDone] = useState(null)
  const [copied,       setCopied]       = useState(null)
  const [selectedIds,  setSelectedIds]  = useState(new Set())
  const [confirmModal, setConfirmModal] = useState(null)   // null | { caseIds: [] }
  const [deleting,     setDeleting]     = useState(false)
  const [shareModal,   setShareModal]   = useState(null)   // null | { caseId, viewerPin }
  const [shareEmail,   setShareEmail]   = useState('')
  const [shareName,    setShareName]    = useState('')
  const [sharing,      setSharing]      = useState(false)
  const [shareMsg,     setShareMsg]     = useState(null)
  const [regenning,    setRegenning]    = useState(null)
  // Care Team
  const [contacts,     setContacts]     = useState([])
  const [contactsLoading, setContactsLoading] = useState(false)
  const [newContactName, setNewContactName] = useState('')
  const [newContactEmail, setNewContactEmail] = useState('')
  const [newContactPhone, setNewContactPhone] = useState('')
  const [newContactRole, setNewContactRole] = useState('LEAD_SURGEON')
  const [addingContact, setAddingContact] = useState(false)
  const [sharingAll,   setSharingAll]   = useState(false)
  // Meet — create modal
  const [meetModal,    setMeetModal]    = useState(null) // null | { caseId, contacts }
  const [meetDate,     setMeetDate]     = useState('')
  const [meetTime,     setMeetTime]     = useState('')
  const [meetDuration, setMeetDuration] = useState(60)
  const [meetNotes,    setMeetNotes]    = useState('')
  const [meetDoctorEmails, setMeetDoctorEmails] = useState('')
  const [meetCreating, setMeetCreating] = useState(false)
  const [meetError,    setMeetError]    = useState(null) // null | string
  const [payModal,     setPayModal]     = useState(null)   // null | { caseId, amount, description, status }
  const [payAmount,    setPayAmount]    = useState('')
  const [payDesc,      setPayDesc]     = useState('')
  const [paySaving,    setPaySaving]    = useState(false)
  const [payErr,       setPayErr]       = useState(null)
  const [payMsg,       setPayMsg]       = useState(null)
  const [payHistory,   setPayHistory]   = useState([])
  const [modelsMap,    setModelsMap]    = useState({})   // caseId → models[]
  const [mLoading,     setMLoading]    = useState(null)
  const [modelUploading, setModelUploading] = useState(null) // caseId while uploading
  const [modelUploadPct, setModelUploadPct] = useState(0)
  const [modelErr,     setModelErr]    = useState(null)
  const [deletingModel, setDeletingModel] = useState(null)
  const [expandedSession, setExpandedSession] = useState(null) // "caseId/sessionKey" to expand validation report
  const [qualityData, setQualityData] = useState({}) // "caseId/sessionKey" → quality result
  const [qualityLoading, setQualityLoading] = useState(null) // sessionKey being checked
  const [annotationsMap, setAnnotationsMap] = useState({}) // caseId → annotations[]
  const [aLoading, setALoading] = useState(null)
  const [dicomMetaMap, setDicomMetaMap] = useState({}) // caseId → { uploadId, open, loading, data, error, copied }
  const [requestingReview, setRequestingReview] = useState(null)
  const modelFileRef                    = useRef(null)
  const searchTimer                     = useRef(null)

  const load = useCallback((p, q) => {
    setLoading(true)
    setSelectedIds(new Set()) // clear selections on reload
    getCases({ page: p, limit: 20, search: q || undefined })
      .then((res) => {
        setCases(res.data.cases)
        setTotal(res.data.total)
        setPages(res.data.pages)
      })
      .catch(() => {})
      .finally(() => setLoading(false))
  }, [])

  useEffect(() => { load(1, '') }, [load])

  function onSearchChange(e) {
    const q = e.target.value
    setSearch(q)
    setPage(1)
    clearTimeout(searchTimer.current)
    searchTimer.current = setTimeout(() => load(1, q), 300)
  }

  async function handleCreate() {
    setCreating(true)
    setCreateErr(null)
    try {
      await createCase(newNotes.trim() || undefined, newCaseId.trim() || undefined)
      setNewNotes('')
      setNewCaseId('')
      setShowCreate(false)
      load(1, search)
    } catch (err) {
      setCreateErr(err.response?.data?.error || 'Failed to create case.')
    } finally {
      setCreating(false)
    }
  }

  async function handleEditSave() {
    if (!editModal) return
    setEditSaving(true)
    setEditErr(null)
    try {
      const payload = {}
      const trimmedId = editModal.caseIdVal.trim().toUpperCase()
      const trimmedNotes = editModal.notesVal.trim()
      if (trimmedId !== editModal.caseId) payload.newCaseId = trimmedId
      payload.notes = trimmedNotes || null
      await updateCase(editModal.caseId, payload)
      // Reload because the caseId key may have changed
      setEditModal(null)
      load(page, search)
    } catch (err) {
      setEditErr(err.response?.data?.error || 'Failed to update case.')
    } finally {
      setEditSaving(false)
    }
  }

  async function handleToggle(caseId) {
    setToggling(caseId)
    try {
      const res = await toggleCaseStatus(caseId)
      setCases((prev) =>
        prev.map((c) => c.caseId === caseId ? { ...c, isActive: res.data.isActive } : c),
      )
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to toggle case status.')
    } finally {
      setToggling(null)
    }
  }

  async function handleToggleDone(caseId) {
    setTogglingDone(caseId)
    try {
      const res = await toggleCaseDone(caseId)
      setCases((prev) =>
        prev.map((c) => c.caseId === caseId ? { ...c, isDone: res.data.isDone } : c),
      )
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to update case status.')
    } finally {
      setTogglingDone(null)
    }
  }

  async function handleExpand(caseId) {
    if (expandedId === caseId) { setExpandedId(null); return }
    setExpandedId(caseId)
    // Load uploads and models in parallel
    if (uploadsMap[caseId] === undefined) {
      setULoading(caseId)
      getUploadsForCase(caseId)
        .then((res) => setUploadsMap((prev) => ({ ...prev, [caseId]: res.data.uploads })))
        .catch(() => setUploadsMap((prev) => ({ ...prev, [caseId]: [] })))
        .finally(() => setULoading(null))
    }
    if (modelsMap[caseId] === undefined) {
      setMLoading(caseId)
      getModelsForCase(caseId)
        .then((res) => setModelsMap((prev) => ({ ...prev, [caseId]: res.data.models })))
        .catch(() => setModelsMap((prev) => ({ ...prev, [caseId]: [] })))
        .finally(() => setMLoading(null))
    }
    if (annotationsMap[caseId] === undefined) {
      setALoading(caseId)
      getCaseAnnotations(caseId)
        .then((res) => setAnnotationsMap((prev) => ({ ...prev, [caseId]: res.data.annotations })))
        .catch(() => setAnnotationsMap((prev) => ({ ...prev, [caseId]: [] })))
        .finally(() => setALoading(null))
    }
  }

  // Accepts one or MANY files (gate finding M2): SSE exports write a
  // `<stl>.sse-meta.json` provenance sidecar beside each STL, and the operator
  // selects both together. Each file goes through the existing single-file
  // endpoint sequentially; sidecar responses (kind 'sse-meta') are metadata,
  // not Model rows, so they don't enter the models list.
  async function handleModelUpload(caseId, files) {
    const list = Array.from(files || []).filter(Boolean)
    if (!list.length) return
    setModelUploading(caseId)
    setModelUploadPct(0)
    setModelErr(null)
    try {
      for (let i = 0; i < list.length; i++) {
        const res = await uploadModel(caseId, list[i], null, (loaded, total) => {
          if (total) {
            const per = 100 / list.length
            setModelUploadPct(Math.round(i * per + (loaded / total) * per))
          }
        })
        if (res.data?.kind !== 'sse-meta') {
          setModelsMap((prev) => ({
            ...prev,
            [caseId]: [res.data, ...(prev[caseId] || [])],
          }))
        }
      }
    } catch (err) {
      setModelErr(err.response?.data?.error || 'Failed to upload model.')
    } finally {
      setModelUploading(null)
      setModelUploadPct(0)
      if (modelFileRef.current) modelFileRef.current.value = ''
    }
  }

  async function handleDeleteModel(caseId, modelId) {
    if (!window.confirm('Delete this model? The file will also be removed from Google Drive.')) return
    setDeletingModel(modelId)
    try {
      await deleteModel(modelId)
      setModelsMap((prev) => ({
        ...prev,
        [caseId]: (prev[caseId] || []).filter((m) => m.id !== modelId),
      }))
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to delete model.')
    } finally {
      setDeletingModel(null)
    }
  }

  async function handleValidateUpload(caseId, uploadId) {
    setValidatingUploadId(uploadId)
    try {
      const res = await validateUpload(uploadId)
      const { validationStatus } = res.data
      setUploadsMap((prev) => ({
        ...prev,
        [caseId]: (prev[caseId] || []).map((u) =>
          u.id === uploadId ? { ...u, validationStatus } : u,
        ),
      }))
    } catch (err) {
      alert(err.response?.data?.error || 'Validation failed. Please try again.')
    } finally {
      setValidatingUploadId(null)
    }
  }

  async function handleQualityCheck(caseId, sessionKey, driveFolderId) {
    if (!driveFolderId) return
    setQualityLoading(sessionKey)
    try {
      const res = await getDicomQuality(caseId, driveFolderId)
      setQualityData((prev) => ({ ...prev, [sessionKey]: res.data }))
      setExpandedSession(`${caseId}/${sessionKey}`)
    } catch {
      setQualityData((prev) => ({ ...prev, [sessionKey]: { error: 'Quality check failed' } }))
    } finally {
      setQualityLoading(null)
    }
  }

  async function handleDeleteUploadSession(caseId, sessionFolder, uploadId) {
    const label = sessionFolder ? `session "${sessionFolder}"` : 'this upload'
    if (!window.confirm(`Delete ${label} and all its files from Drive?`)) return
    const key = sessionFolder || uploadId
    setDeletingSession(key)
    try {
      await deleteUploadSession(caseId, { sessionFolder, uploadId })
      // Remove deleted uploads from local state
      setUploadsMap((prev) => ({
        ...prev,
        [caseId]: (prev[caseId] || []).filter((u) =>
          sessionFolder ? u.sessionFolder !== sessionFolder : u.id !== uploadId,
        ),
      }))
      // Refresh case list to update upload count
      load(page, search)
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to delete uploads.')
    } finally {
      setDeletingSession(null)
    }
  }

  function copyUploadLink(caseId) {
    navigator.clipboard.writeText(`${window.location.origin}/upload/${caseId}`)
      .then(() => {
        setCopied(caseId)
        setTimeout(() => setCopied(null), 2000)
      })
  }

  /**
   * Open the scan order slip for this case.
   *
   * The slip is the requisition the imaging centre acts on: the six rules, what the
   * procedure needs, and the settings for the scanner being used. It lives on the marketing
   * site rather than the portal because it has to survive being forwarded on WhatsApp to
   * someone with no portal login.
   *
   * Only the case reference is passed. The procedure is chosen in the builder, because the
   * case record has no procedure field yet — adding one is a migration.
   */
  function openScanOrder(caseId) {
    window.open(`https://www.osteo3d.com/scan-order/?case=${encodeURIComponent(caseId)}`,
                '_blank', 'noopener')
  }

  function changePage(newPage) {
    setPage(newPage)
    load(newPage, search)
  }

  // ── Checkbox helpers ──────────────────────────────────────────
  function toggleSelect(caseId) {
    setSelectedIds((prev) => {
      const next = new Set(prev)
      next.has(caseId) ? next.delete(caseId) : next.add(caseId)
      return next
    })
  }

  const allOnPageSelected = cases.length > 0 && cases.every((c) => selectedIds.has(c.caseId))

  function toggleSelectAll() {
    if (allOnPageSelected) {
      setSelectedIds(new Set())
    } else {
      setSelectedIds(new Set(cases.map((c) => c.caseId)))
    }
  }

  // ── Delete handlers ───────────────────────────────────────────
  async function handleDeleteConfirm() {
    if (!confirmModal) return
    setDeleting(true)
    try {
      if (confirmModal.caseIds.length === 1) {
        await deleteCase(confirmModal.caseIds[0])
      } else {
        await deleteCases(confirmModal.caseIds)
      }
      setConfirmModal(null)
      setSelectedIds(new Set())
      // Reload — go back to page 1 if we deleted the whole current page
      load(page, search)
    } catch (err) {
      alert(err.response?.data?.error || 'Delete failed. Please try again.')
    } finally {
      setDeleting(false)
    }
  }

  async function openShareModal(c) {
    setShareModal({ caseId: c.caseId, viewerPin: c.viewerPin })
    setShareEmail('')
    setShareName('')
    setShareMsg(null)
    setNewContactName('')
    setNewContactEmail('')
    setNewContactPhone('')
    setNewContactRole('LEAD_SURGEON')
    // Load care team
    setContactsLoading(true)
    try {
      const res = await getCaseContacts(c.caseId)
      setContacts(res.data.contacts || [])
    } catch {
      setContacts([])
    } finally {
      setContactsLoading(false)
    }
  }

  async function handleShare() {
    if (!shareModal) return
    setSharing(true)
    setShareMsg(null)
    try {
      const res = await shareCase(shareModal.caseId, shareEmail.trim(), shareName.trim() || undefined)
      setShareMsg({ ok: true, text: res.data.message })
      setShareEmail('')
      setShareName('')
    } catch (err) {
      setShareMsg({ ok: false, text: err.response?.data?.error || 'Failed to send email.' })
    } finally {
      setSharing(false)
    }
  }

  async function handleAddContact() {
    if (!shareModal || !newContactName.trim() || !newContactEmail.trim()) return
    setAddingContact(true)
    try {
      const res = await addCaseContact(shareModal.caseId, {
        name: newContactName.trim(),
        email: newContactEmail.trim(),
        phone: newContactPhone.trim() || undefined,
        role: newContactRole,
      })
      setContacts((prev) => [...prev, res.data.contact])
      setNewContactName('')
      setNewContactEmail('')
      setNewContactPhone('')
      setNewContactRole('LEAD_SURGEON')
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to add contact.')
    } finally {
      setAddingContact(false)
    }
  }

  async function handleDeleteContact(id) {
    if (!confirm('Remove this contact?')) return
    try {
      await deleteCaseContact(id)
      setContacts((prev) => prev.filter((c) => c.id !== id))
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to remove contact.')
    }
  }

  async function handleShareContact(contact) {
    if (!shareModal) return
    setSharing(true)
    setShareMsg(null)
    try {
      const res = await shareCase(shareModal.caseId, contact.email, contact.name, contact.id)
      setShareMsg({ ok: true, text: res.data.message })
      setContacts((prev) => prev.map((c) => c.id === contact.id ? { ...c, sharedAt: new Date().toISOString() } : c))
    } catch (err) {
      setShareMsg({ ok: false, text: err.response?.data?.error || 'Failed to send email.' })
    } finally {
      setSharing(false)
    }
  }

  async function handleShareAll() {
    if (!shareModal) return
    setSharingAll(true)
    setShareMsg(null)
    try {
      const res = await shareAllContacts(shareModal.caseId)
      setShareMsg({ ok: true, text: res.data.message })
      // Refresh contacts to get updated sharedAt
      const updated = await getCaseContacts(shareModal.caseId)
      setContacts(updated.data.contacts || [])
    } catch (err) {
      setShareMsg({ ok: false, text: err.response?.data?.error || 'Failed to share.' })
    } finally {
      setSharingAll(false)
    }
  }

  async function handleRegenPin(caseId) {
    setRegenning(caseId)
    try {
      const res = await regeneratePin(caseId)
      // Update local state
      setCases((prev) =>
        prev.map((c) => c.caseId === caseId ? { ...c, viewerPin: res.data.viewerPin } : c),
      )
      if (shareModal?.caseId === caseId) {
        setShareModal((m) => ({ ...m, viewerPin: res.data.viewerPin }))
      }
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to regenerate PIN.')
    } finally {
      setRegenning(null)
    }
  }

  // ── Meet helpers ─────────────────────────────────────────────
  async function openMeetModal(c) {
    try {
      const { data } = await getCaseContacts(c.caseId)
      setMeetModal({ caseId: c.caseId, contacts: data.contacts || [] })
    } catch {
      setMeetModal({ caseId: c.caseId, contacts: [] })
    }
    setMeetDate('')
    setMeetTime('')
    setMeetDuration(60)
    setMeetNotes('')
    setMeetDoctorEmails('')
    setMeetError(null)
  }

  function parseDoctorEmails(raw, existingEmails = []) {
    const existing = new Set(existingEmails.map(e => e.toLowerCase()))
    const seen = new Set()
    const valid = []
    const invalid = []
    for (const tok of String(raw).split(/[,\s;]+/)) {
      const e = tok.trim().toLowerCase()
      if (!e) continue
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(e)) { invalid.push(tok.trim()); continue }
      if (seen.has(e) || existing.has(e)) continue
      seen.add(e)
      valid.push(e)
    }
    return { valid, invalid }
  }

  async function handleCreateMeet() {
    if (!meetModal) return
    if (!meetDate || !meetTime) {
      setMeetError('Please select both date and time')
      return
    }
    const existingEmails = (meetModal.contacts || []).map(c => c.email).filter(Boolean)
    const { valid: doctorEmails, invalid } = parseDoctorEmails(meetDoctorEmails, existingEmails)
    if (invalid.length) {
      setMeetError(`Invalid email${invalid.length > 1 ? 's' : ''}: ${invalid.join(', ')}`)
      return
    }
    setMeetCreating(true)
    setMeetError(null)
    try {
      const scheduledAt = new Date(`${meetDate}T${meetTime}`).toISOString()
      const { data } = await createMeeting(meetModal.caseId, {
        scheduledAt,
        durationMin: meetDuration,
        notes: meetNotes || undefined,
        doctorEmails: doctorEmails.length ? doctorEmails : undefined,
      })
      setCases(prev => prev.map(c =>
        c.caseId === meetModal.caseId
          ? { ...c, nextMeeting: { id: data.id, meetUrl: data.meetUrl, scheduledAt: data.scheduledAt, durationMin: data.durationMin } }
          : c
      ))
      setMeetModal(null)
    } catch (err) {
      const code = err.response?.data?.code
      if (code === 'CALENDAR_NOT_CONNECTED') {
        setMeetError('CALENDAR_NOT_CONNECTED')
      } else {
        setMeetError(err.response?.data?.error || 'Failed to create meeting')
      }
    } finally {
      setMeetCreating(false)
    }
  }

  function openPayModal(c) {
    setPayModal({
      caseId: c.caseId,
      amount: c.paymentAmount || '',
      description: c.paymentDescription || '',
      status: c.paymentStatus || 'NONE',
    })
    setPayAmount(c.paymentAmount ? String(Number(c.paymentAmount)) : '')
    setPayDesc(c.paymentDescription || '')
    setPayErr(null)
    setPayMsg(null)
    setPayHistory([])
    getPaymentHistory(c.caseId)
      .then((res) => setPayHistory(res.data.payments || []))
      .catch(() => {})
  }

  async function handlePaySave() {
    if (!payModal) return
    const num = Number(payAmount)
    if (payAmount !== '' && (isNaN(num) || num < 0)) {
      setPayErr('Enter a valid amount (0 or more).')
      return
    }
    setPaySaving(true)
    setPayErr(null)
    try {
      const res = await setPaymentAmount(payModal.caseId, payAmount === '' ? 0 : num, payDesc.trim())
      setCases((prev) =>
        prev.map((c) => c.caseId === payModal.caseId ? {
          ...c,
          paymentAmount: res.data.paymentAmount,
          paymentStatus: res.data.paymentStatus,
          paymentDescription: res.data.paymentDescription,
        } : c),
      )
      setPayMsg({ ok: true, text: 'Payment configuration saved.' })
      setPayModal((m) => ({ ...m, status: res.data.paymentStatus }))
    } catch (err) {
      setPayErr(err.response?.data?.error || 'Failed to update payment.')
    } finally {
      setPaySaving(false)
    }
  }

  async function handleWaive() {
    if (!payModal) return
    setPaySaving(true)
    setPayErr(null)
    try {
      const res = await waivePayment(payModal.caseId)
      setCases((prev) =>
        prev.map((c) => c.caseId === payModal.caseId ? { ...c, paymentStatus: res.data.paymentStatus } : c),
      )
      setPayModal((m) => ({ ...m, status: res.data.paymentStatus }))
      setPayMsg({ ok: true, text: 'Payment waived.' })
    } catch (err) {
      setPayErr(err.response?.data?.error || 'Failed to waive payment.')
    } finally {
      setPaySaving(false)
    }
  }

  function copyPayLink(caseId) {
    navigator.clipboard.writeText(`${window.location.origin}/pay/${caseId}`)
      .then(() => {
        setCopied(`pay-${caseId}`)
        setTimeout(() => setCopied(null), 2000)
      })
  }

  return (
    <div className="space-y-4">

      {/* Confirmation modal */}
      {confirmModal && (
        <ConfirmDeleteModal
          caseIds={confirmModal.caseIds}
          onConfirm={handleDeleteConfirm}
          onCancel={() => setConfirmModal(null)}
          loading={deleting}
        />
      )}

      {/* Edit case modal */}
      {editModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-sm mx-4 p-6">
            <h3 className="text-sm font-semibold text-gray-800 mb-4">Edit Case</h3>
            {editErr && <p className="text-xs text-red-500 mb-3">{editErr}</p>}
            <div className="space-y-3">
              <div>
                <label className="text-xs text-gray-500 block mb-1">Case ID</label>
                <input
                  type="text"
                  value={editModal.caseIdVal}
                  onChange={(e) => setEditModal((m) => ({ ...m, caseIdVal: e.target.value.toUpperCase().replace(/\s/g, '') }))}
                  onKeyDown={(e) => e.key === 'Enter' && handleEditSave()}
                  className={`w-full border border-gray-300 rounded-lg px-3 py-2 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-brand-300 ${!isAdmin ? 'bg-gray-100 cursor-not-allowed' : ''}`}
                  autoFocus
                  readOnly={!isAdmin}
                />
                <p className="text-[11px] text-gray-400 mt-1">
                  {isAdmin ? 'Uppercase letters, digits, hyphens, underscores (3–50 chars)' : 'Only admins can rename case IDs'}
                </p>
              </div>
              <div>
                <label className="text-xs text-gray-500 block mb-1">Notes (optional)</label>
                <input
                  type="text"
                  value={editModal.notesVal}
                  onChange={(e) => setEditModal((m) => ({ ...m, notesVal: e.target.value }))}
                  onKeyDown={(e) => e.key === 'Enter' && handleEditSave()}
                  placeholder="Patient name, scan type, …"
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-300"
                />
              </div>
            </div>
            <div className="flex gap-2 justify-end mt-5">
              <button
                onClick={() => { setEditModal(null); setEditErr(null) }}
                disabled={editSaving}
                className="text-sm px-4 py-2 rounded-lg border border-gray-200 text-gray-600 hover:bg-gray-50 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleEditSave}
                disabled={editSaving}
                className="text-sm px-4 py-2 rounded-lg bg-brand-600 text-white hover:bg-brand-700 transition-colors disabled:opacity-60 flex items-center gap-2"
              >
                {editSaving && (
                  <svg className="w-3.5 h-3.5 animate-spin" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
                  </svg>
                )}
                {editSaving ? 'Saving…' : 'Save changes'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Share / Care Team modal */}
      {shareModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-lg mx-4 p-6 max-h-[90vh] overflow-y-auto">
            <h3 className="text-sm font-semibold text-gray-800 mb-1">Share Case &amp; Care Team</h3>
            <p className="text-xs text-gray-400 mb-4">
              Manage care team and share case credentials.
            </p>

            {/* PIN display */}
            <div className="bg-gray-50 rounded-lg px-4 py-3 mb-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-[11px] text-gray-400">Case ID</p>
                  <p className="font-mono text-sm font-semibold text-gray-900">{shareModal.caseId}</p>
                </div>
                <div className="text-right">
                  <p className="text-[11px] text-gray-400">Viewer PIN</p>
                  <p className="font-mono text-lg font-bold text-brand-700 tracking-wider">
                    {shareModal.viewerPin || '—'}
                  </p>
                </div>
              </div>
              {isAdmin && <button
                onClick={() => handleRegenPin(shareModal.caseId)}
                disabled={regenning === shareModal.caseId}
                className="mt-2 text-[11px] text-gray-400 hover:text-brand-600 transition-colors disabled:opacity-50"
              >
                {regenning === shareModal.caseId ? 'Regenerating…' : 'Regenerate PIN'}
              </button>}
            </div>

            {shareMsg && (
              <p className={`text-xs mb-3 ${shareMsg.ok ? 'text-green-600' : 'text-red-500'}`}>{shareMsg.text}</p>
            )}

            {/* ── Care Team ── */}
            <div className="mb-4">
              <div className="flex items-center justify-between mb-2">
                <h4 className="text-xs font-semibold text-gray-600 uppercase tracking-wide">Care Team</h4>
                {contacts.filter((c) => !c.sharedAt).length > 0 && (
                  <button
                    onClick={handleShareAll}
                    disabled={sharingAll || sharing}
                    className="text-[11px] px-2.5 py-1 rounded-md bg-brand-50 text-brand-700 hover:bg-brand-100 transition-colors disabled:opacity-50"
                  >
                    {sharingAll ? 'Sharing…' : `Share All (${contacts.filter((c) => !c.sharedAt).length})`}
                  </button>
                )}
              </div>

              {contactsLoading ? (
                <p className="text-xs text-gray-400 py-2">Loading…</p>
              ) : contacts.length === 0 ? (
                <p className="text-xs text-gray-400 py-2">No contacts added yet. Add a doctor below.</p>
              ) : (
                <div className="space-y-2 mb-3">
                  {contacts.map((c) => (
                    <div key={c.id} className="flex items-center gap-2 bg-gray-50 rounded-lg px-3 py-2">
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-gray-800 truncate">{c.name}</p>
                        <p className="text-[11px] text-gray-400 truncate">{c.email} · {c.role.replace(/_/g, ' ').toLowerCase()}</p>
                      </div>
                      {/* Approval status */}
                      {c.approvalStatus === 'APPROVED' && (
                        <span className="shrink-0 text-[10px] px-2 py-0.5 rounded-full bg-green-100 text-green-700 font-medium" title={c.approvalComment || ''}>Approved</span>
                      )}
                      {c.approvalStatus === 'CHANGES_REQUESTED' && (
                        <span className="shrink-0 text-[10px] px-2 py-0.5 rounded-full bg-amber-100 text-amber-700 font-medium" title={c.approvalComment || ''}>Changes</span>
                      )}
                      {/* Share status */}
                      {c.sharedAt ? (
                        <span className="shrink-0 text-[10px] text-green-600">Shared</span>
                      ) : (
                        <button
                          onClick={() => handleShareContact(c)}
                          disabled={sharing}
                          className="shrink-0 text-[11px] px-2 py-1 rounded-md bg-brand-600 text-white hover:bg-brand-700 transition-colors disabled:opacity-50"
                        >
                          Share
                        </button>
                      )}
                      <button
                        onClick={() => handleDeleteContact(c.id)}
                        className="shrink-0 text-gray-300 hover:text-red-500 transition-colors"
                        title="Remove"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      </button>
                    </div>
                  ))}
                </div>
              )}

              {/* Add contact form */}
              <div className="border border-dashed border-gray-300 rounded-lg p-3">
                <p className="text-[11px] text-gray-500 font-medium mb-2">Add Doctor</p>
                <div className="grid grid-cols-2 gap-2 mb-2">
                  <input
                    type="text"
                    value={newContactName}
                    onChange={(e) => setNewContactName(e.target.value)}
                    placeholder="Name (Dr. …)"
                    className="col-span-2 border border-gray-300 rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-brand-300"
                  />
                  <input
                    type="email"
                    value={newContactEmail}
                    onChange={(e) => setNewContactEmail(e.target.value)}
                    placeholder="Email"
                    className="border border-gray-300 rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-brand-300"
                  />
                  <input
                    type="tel"
                    value={newContactPhone}
                    onChange={(e) => setNewContactPhone(e.target.value)}
                    placeholder="Phone (optional)"
                    className="border border-gray-300 rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-brand-300"
                  />
                </div>
                <div className="flex items-center gap-2">
                  <select
                    value={newContactRole}
                    onChange={(e) => setNewContactRole(e.target.value)}
                    className="flex-1 border border-gray-300 rounded-lg px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-brand-300"
                  >
                    <option value="LEAD_SURGEON">Lead Surgeon</option>
                    <option value="ASSISTING_SURGEON">Assisting Surgeon</option>
                    <option value="REFERRING_DOCTOR">Referring Doctor</option>
                    <option value="OTHER">Other</option>
                  </select>
                  <button
                    onClick={handleAddContact}
                    disabled={addingContact || !newContactName.trim() || !newContactEmail.trim()}
                    className="text-sm px-3 py-1.5 rounded-lg bg-gray-800 text-white hover:bg-gray-900 transition-colors disabled:opacity-50"
                  >
                    {addingContact ? 'Adding…' : 'Add'}
                  </button>
                </div>
              </div>
            </div>

            {/* ── Quick share (one-off, without adding to care team) ── */}
            <details className="mb-4">
              <summary className="text-xs font-semibold text-gray-500 cursor-pointer hover:text-gray-700 transition-colors">
                Quick share (without adding to care team)
              </summary>
              <div className="mt-3 space-y-3">
                <div>
                  <label className="text-xs text-gray-500 block mb-1">Surgeon Name</label>
                  <input
                    type="text"
                    value={shareName}
                    onChange={(e) => setShareName(e.target.value)}
                    placeholder="Dr. …"
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-300"
                  />
                </div>
                <div>
                  <label className="text-xs text-gray-500 block mb-1">Surgeon Email</label>
                  <input
                    type="email"
                    value={shareEmail}
                    onChange={(e) => setShareEmail(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && shareEmail.trim() && handleShare()}
                    placeholder="surgeon@hospital.com"
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-300"
                  />
                </div>
                <button
                  onClick={handleShare}
                  disabled={sharing || !shareEmail.trim()}
                  className="text-sm px-4 py-2 rounded-lg bg-brand-600 text-white hover:bg-brand-700 transition-colors disabled:opacity-60 flex items-center gap-2"
                >
                  {sharing && (
                    <svg className="w-3.5 h-3.5 animate-spin" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
                    </svg>
                  )}
                  {sharing ? 'Sending…' : 'Send Email'}
                </button>
              </div>
            </details>

            <div className="flex justify-end">
              <button
                onClick={() => { setShareModal(null); setShareMsg(null) }}
                disabled={sharing || sharingAll}
                className="text-sm px-4 py-2 rounded-lg border border-gray-200 text-gray-600 hover:bg-gray-50 transition-colors"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Payment modal */}
      {payModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-md mx-4 p-6 max-h-[90vh] overflow-y-auto">
            <h3 className="text-sm font-semibold text-gray-800 mb-1">Payment Settings</h3>
            <p className="text-xs text-gray-400 mb-4">
              Case <span className="font-mono font-semibold">{payModal.caseId}</span>
              {payModal.status !== 'NONE' && (
                <span className={`ml-2 px-1.5 py-0.5 rounded-full text-[10px] font-medium ${PAY_PILL[payModal.status] || 'bg-gray-100 text-gray-500'}`}>
                  {payModal.status}
                </span>
              )}
            </p>

            {payErr && <p className="text-xs text-red-500 mb-3">{payErr}</p>}
            {payMsg && <p className={`text-xs mb-3 ${payMsg.ok ? 'text-green-600' : 'text-red-500'}`}>{payMsg.text}</p>}

            {/* Payment history */}
            {payHistory.length > 0 && (
              <div className="mb-4">
                <p className="text-xs font-medium text-gray-500 mb-2">Payment History</p>
                <div className="space-y-1.5 max-h-32 overflow-y-auto">
                  {payHistory.map((p) => (
                    <div key={p.id} className="flex items-center justify-between text-xs bg-gray-50 rounded-lg px-3 py-2">
                      <div>
                        <span className="font-mono font-medium">INR {Number(p.amount).toLocaleString('en-IN')}</span>
                        {p.payerName && <span className="text-gray-400 ml-2">{p.payerName}</span>}
                      </div>
                      <div className="flex items-center gap-2">
                        <span className={`px-1.5 py-0.5 rounded-full text-[10px] font-medium ${
                          p.status === 'CAPTURED' ? 'bg-green-100 text-green-700' :
                          p.status === 'FAILED' ? 'bg-red-100 text-red-700' :
                          'bg-gray-100 text-gray-500'
                        }`}>{p.status}</span>
                        <span className="text-gray-400">
                          {new Date(p.capturedAt || p.createdAt).toLocaleDateString('en-IN', { day: '2-digit', month: 'short' })}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* New payment form */}
            {(payModal.status === 'PAID' || payModal.status === 'WAIVED') && (
              <p className="text-xs text-blue-600 bg-blue-50 rounded-lg px-3 py-2 mb-3">
                Set a new amount below to create another payment request for this case.
              </p>
            )}

            {isAdmin ? (
              <>
                <div className="space-y-3">
                  <div>
                    <label className="text-xs text-gray-500 block mb-1">Amount (INR)</label>
                    <input
                      type="number"
                      min="0"
                      step="0.01"
                      value={payAmount}
                      onChange={(e) => setPayAmount(e.target.value)}
                      onKeyDown={(e) => e.key === 'Enter' && handlePaySave()}
                      placeholder="0.00"
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-brand-300"
                      autoFocus
                    />
                  </div>
                  <div>
                    <label className="text-xs text-gray-500 block mb-1">Description (optional)</label>
                    <input
                      type="text"
                      value={payDesc}
                      onChange={(e) => setPayDesc(e.target.value)}
                      onKeyDown={(e) => e.key === 'Enter' && handlePaySave()}
                      placeholder="e.g. VSP Planning Fee"
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-300"
                    />
                  </div>
                </div>

                {payModal.status === 'PENDING' && (
                  <button
                    onClick={() => copyPayLink(payModal.caseId)}
                    className="mt-3 text-xs text-brand-600 hover:text-brand-700 transition-colors"
                  >
                    {copied === `pay-${payModal.caseId}` ? '✓ Link copied' : 'Copy payment link'}
                  </button>
                )}

                <div className="flex gap-2 justify-between mt-5">
                  <div>
                    {payModal.status === 'PENDING' && (
                      <button
                        onClick={handleWaive}
                        disabled={paySaving}
                        className="text-xs px-3 py-2 rounded-lg border border-yellow-200 bg-yellow-50 text-yellow-700 hover:bg-yellow-100 transition-colors disabled:opacity-50"
                      >
                        Waive Payment
                      </button>
                    )}
                  </div>
                  <div className="flex gap-2">
                    <button
                      onClick={() => { setPayModal(null); setPayErr(null); setPayMsg(null) }}
                      disabled={paySaving}
                      className="text-sm px-4 py-2 rounded-lg border border-gray-200 text-gray-600 hover:bg-gray-50 transition-colors"
                    >
                      Close
                    </button>
                    <button
                      onClick={handlePaySave}
                      disabled={paySaving}
                      className="text-sm px-4 py-2 rounded-lg bg-brand-600 text-white hover:bg-brand-700 transition-colors disabled:opacity-60 flex items-center gap-2"
                    >
                      {paySaving && (
                        <svg className="w-3.5 h-3.5 animate-spin" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
                        </svg>
                      )}
                      {paySaving ? 'Saving…' : (payModal.status === 'PAID' || payModal.status === 'WAIVED') ? 'Set New Payment' : 'Save'}
                    </button>
                  </div>
                </div>
              </>
            ) : (
              <div className="mt-4">
                <button
                  onClick={() => { setPayModal(null); setPayErr(null); setPayMsg(null) }}
                  className="text-sm px-4 py-2 rounded-lg border border-gray-200 text-gray-600 hover:bg-gray-50 transition-colors"
                >
                  Close
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Toolbar */}
      <div className="flex items-center gap-3">
        <input
          type="text"
          value={search}
          onChange={onSearchChange}
          placeholder="Search case IDs…"
          className="flex-1 border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-300"
        />
        <button
          onClick={() => { setShowCreate((v) => !v); setCreateErr(null) }}
          className="btn-primary text-sm whitespace-nowrap"
        >
          {showCreate ? 'Cancel' : '+ New Case'}
        </button>
      </div>

      {/* Bulk action bar — visible when ≥1 row is selected (admin only) */}
      {isAdmin && selectedIds.size > 0 && (
        <div className="flex items-center justify-between bg-red-50 border border-red-200 rounded-xl px-4 py-2.5">
          <span className="text-sm text-red-700 font-medium">
            {selectedIds.size} {selectedIds.size === 1 ? 'case' : 'cases'} selected
          </span>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setSelectedIds(new Set())}
              className="text-xs text-red-500 hover:text-red-700 transition-colors"
            >
              Clear selection
            </button>
            <button
              onClick={() => setConfirmModal({ caseIds: Array.from(selectedIds) })}
              className="text-xs px-3 py-1.5 rounded-lg bg-red-600 text-white hover:bg-red-700 transition-colors font-medium"
            >
              Delete {selectedIds.size} {selectedIds.size === 1 ? 'case' : 'cases'}
            </button>
          </div>
        </div>
      )}

      {/* Create form */}
      {showCreate && (
        <div className="bg-white rounded-xl border border-gray-200 p-5">
          <h3 className="text-sm font-semibold text-gray-700 mb-3">New Case</h3>
          {createErr && <p className="text-xs text-red-500 mb-2">{createErr}</p>}
          <div className="flex items-end gap-3 flex-wrap">
            <div className="w-52">
              <label className="text-xs text-gray-500 block mb-1">Case ID (optional)</label>
              <input
                type="text"
                value={newCaseId}
                onChange={(e) => setNewCaseId(e.target.value.toUpperCase().replace(/\s/g, ''))}
                onKeyDown={(e) => e.key === 'Enter' && handleCreate()}
                placeholder="Leave blank to auto-generate"
                maxLength={50}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-brand-300"
              />
            </div>
            <div className="flex-1 min-w-[160px]">
              <label className="text-xs text-gray-500 block mb-1">Notes (optional)</label>
              <input
                type="text"
                value={newNotes}
                onChange={(e) => setNewNotes(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleCreate()}
                placeholder="Patient name, scan type, …"
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-300"
              />
            </div>
            <button onClick={handleCreate} disabled={creating} className="btn-primary text-sm">
              {creating ? 'Creating…' : 'Create Case'}
            </button>
          </div>
          <p className="text-xs text-gray-400 mt-2">
            Leave Case ID blank to auto-generate (e.g. O3D-K7QM2P9X), or enter a custom ID for externally assigned cases.
          </p>
        </div>
      )}

      {/* Table */}
      <div className="bg-white rounded-xl border border-gray-200 overflow-x-auto">
        {loading ? (
          <div className="py-12 flex justify-center"><CentredSpinner /></div>
        ) : cases.length === 0 ? (
          <div className="py-12 text-center text-gray-400 text-sm">
            {search ? 'No cases match your search.' : 'No cases yet — create one above.'}
          </div>
        ) : (
          <table className="w-full text-sm min-w-[900px]">
            <thead>
              <tr className="border-b border-gray-100 text-[11px] text-gray-400 uppercase tracking-wide bg-gray-50">
                <th className="pl-4 pr-2 py-3">
                  {isAdmin && <input
                    type="checkbox"
                    checked={allOnPageSelected}
                    onChange={toggleSelectAll}
                    className="w-3.5 h-3.5 accent-brand-600 cursor-pointer"
                    title={allOnPageSelected ? 'Deselect all' : 'Select all on page'}
                  />}
                </th>
                <th className="px-3 py-3 text-left font-medium">Case ID</th>
                <th className="px-3 py-3 text-left font-medium">Created</th>
                <th className="px-3 py-3 text-left font-medium">Status</th>
                <th className="px-3 py-3 text-center font-medium">Uploads</th>
                <th className="px-3 py-3 text-center font-medium">✗ Val</th>
                <th className="px-3 py-3 text-center font-medium">PIN</th>
                <th className="px-3 py-3 text-center font-medium">Payment</th>
                <th className="px-3 py-3 text-center font-medium">Approval</th>
                <th className="px-3 py-3 text-right font-medium sticky right-0 bg-gray-50">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {cases.map((c) => (
                <Fragment key={c.caseId}>

                  {/* Case row */}
                  <tr className={`group hover:bg-gray-50 transition-colors ${selectedIds.has(c.caseId) ? 'bg-red-50/40' : ''}`}>
                    <td className="pl-4 pr-2 py-3">
                      {isAdmin && <input
                        type="checkbox"
                        checked={selectedIds.has(c.caseId)}
                        onChange={() => toggleSelect(c.caseId)}
                        className="w-3.5 h-3.5 accent-brand-600 cursor-pointer"
                      />}
                    </td>
                    <td className="px-3 py-3">
                      <p className="font-mono text-sm font-semibold text-gray-900">{c.caseId}</p>
                      {c.notes && (
                        <p className="text-xs text-gray-400 truncate max-w-[200px] mt-0.5">{c.notes}</p>
                      )}
                    </td>
                    <td className="px-3 py-3 text-gray-500 text-xs whitespace-nowrap">{formatDate(c.createdAt)}</td>
                    <td className="px-3 py-3">
                      {c.isDone ? (
                        <button
                          onClick={() => handleToggleDone(c.caseId)}
                          disabled={togglingDone === c.caseId}
                          title="Click to mark as Active"
                          className="text-xs font-medium px-2 py-0.5 rounded-full bg-blue-100 text-blue-700 hover:bg-blue-200 cursor-pointer transition-colors disabled:opacity-50"
                        >
                          {togglingDone === c.caseId ? '…' : '✓ Done'}
                        </button>
                      ) : c.isActive ? (
                        <button
                          onClick={() => handleToggleDone(c.caseId)}
                          disabled={togglingDone === c.caseId}
                          title="Click to mark as Done"
                          className="text-xs font-medium px-2 py-0.5 rounded-full bg-green-100 text-green-700 hover:bg-green-200 cursor-pointer transition-colors disabled:opacity-50"
                        >
                          {togglingDone === c.caseId ? '…' : 'Active'}
                        </button>
                      ) : (
                        <span className="text-xs font-medium px-2 py-0.5 rounded-full bg-amber-100 text-amber-700">
                          On Hold
                        </span>
                      )}
                    </td>
                    <td className="px-3 py-3 text-center text-gray-700">{c.uploadCount}</td>
                    <td className="px-3 py-3 text-center">
                      <span className={c.failedValidationCount > 0 ? 'text-red-600 font-semibold' : 'text-gray-300'}>
                        {c.failedValidationCount}
                      </span>
                    </td>
                    <td className="px-3 py-3 text-center">
                      <span className="font-mono text-xs text-gray-500">{c.viewerPin || '—'}</span>
                    </td>
                    <td className="px-3 py-3 text-center">
                      {c.paymentStatus !== 'NONE' ? (
                        <button
                          onClick={() => openPayModal(c)}
                          className={`text-[10px] font-medium px-2 py-0.5 rounded-full cursor-pointer hover:opacity-80 transition-opacity ${PAY_PILL[c.paymentStatus] || PAY_PILL.NONE}`}
                          title={c.paymentAmount ? `INR ${Number(c.paymentAmount).toLocaleString('en-IN')}` : ''}
                        >
                          {c.paymentStatus}
                        </button>
                      ) : (
                        <span className="text-gray-300 text-xs">—</span>
                      )}
                    </td>
                    <td className="px-3 py-3 text-center">
                      {c.approval ? (
                        <button
                          onClick={() => openShareModal(c)}
                          className={`text-[10px] font-medium px-2 py-0.5 rounded-full cursor-pointer hover:opacity-80 transition-opacity ${
                            c.approval.changes > 0
                              ? 'bg-red-100 text-red-700'
                              : c.approval.approved === c.approval.shared
                                ? 'bg-green-100 text-green-700'
                                : 'bg-yellow-100 text-yellow-700'
                          }`}
                          title={`${c.approval.approved} approved, ${c.approval.changes} changes requested, ${c.approval.pending} pending`}
                        >
                          {c.approval.changes > 0
                            ? `Changes (${c.approval.changes})`
                            : c.approval.approved === c.approval.shared
                              ? `Approved (${c.approval.approved}/${c.approval.shared})`
                              : `${c.approval.approved}/${c.approval.shared}`
                          }
                        </button>
                      ) : (
                        <span className="text-gray-300 text-xs">—</span>
                      )}
                    </td>
                    <td className="px-3 py-3 whitespace-nowrap sticky right-0 bg-white group-hover:bg-gray-50">
                      <div className="flex items-center justify-end gap-1.5">
                        <button
                          onClick={() => openShareModal(c)}
                          className="text-xs px-2 py-1 rounded-lg border border-brand-200 bg-brand-50 text-brand-700 hover:bg-brand-100 transition-colors font-medium"
                          title="Share case with surgeon via email"
                        >
                          Share
                        </button>
                        {(c.uploadCount > 0 || c.driveFolderId || uploadsMap[c.caseId]?.length > 0) && (
                          <button
                            onClick={() => {
                              const token = sessionStorage.getItem('adminToken')
                              const studioUrl = import.meta.env.DEV
                                ? `http://localhost:5176/review/${c.caseId}?token=${token}`
                                : `https://studio.osteo3d.com/review/${c.caseId}?token=${token}`
                              window.open(studioUrl, '_blank')
                            }}
                            className="text-xs px-2 py-1 rounded-lg border border-purple-200 bg-purple-50 text-purple-700 hover:bg-purple-100 transition-colors font-medium"
                            title="Review DICOM files in Studio"
                          >
                            Review
                          </button>
                        )}
                        {c.nextMeeting ? (
                          <a
                            href={c.nextMeeting.meetUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-xs px-2 py-1 rounded-lg border border-green-200 bg-green-50 text-green-700 hover:bg-green-100 transition-colors font-medium"
                            title={`Meet scheduled ${formatDate(c.nextMeeting.scheduledAt)} — click to join`}
                          >
                            Meet {formatDate(c.nextMeeting.scheduledAt)}
                          </a>
                        ) : (
                          <button
                            onClick={() => openMeetModal(c)}
                            className="text-xs px-2 py-1 rounded-lg border border-teal-200 bg-teal-50 text-teal-700 hover:bg-teal-100 transition-colors font-medium"
                            title="Schedule Google Meet session"
                          >
                            Meet
                          </button>
                        )}
                        <button
                          onClick={() => openPayModal(c)}
                          className="text-xs px-2 py-1 rounded-lg border border-orange-200 bg-orange-50 text-orange-700 hover:bg-orange-100 transition-colors font-medium"
                          title="Configure payment for this case"
                        >
                          Pay
                        </button>
                        <button
                          onClick={() => copyUploadLink(c.caseId)}
                          className="text-xs px-2 py-1 rounded-lg border border-gray-200 text-gray-500 hover:bg-gray-50 transition-colors"
                          title="Copy upload link for patient"
                        >
                          {copied === c.caseId ? '✓ Copied' : 'Copy link'}
                        </button>
                        <button
                          onClick={() => openScanOrder(c.caseId)}
                          className="text-xs px-2 py-1 rounded-lg border border-gray-200 text-gray-500 hover:bg-gray-50 transition-colors"
                          title="Scan order slip to send to the imaging centre"
                        >
                          Scan order
                        </button>
                        {isAdmin && <button
                          onClick={() => handleToggle(c.caseId)}
                          disabled={toggling === c.caseId}
                          className="text-xs px-2 py-1 rounded-lg border border-gray-200 text-gray-600 hover:bg-gray-50 transition-colors disabled:opacity-50"
                        >
                          {toggling === c.caseId ? '…' : c.isActive ? 'Hold' : 'Resume'}
                        </button>}
                        {/* Edit icon */}
                        <button
                          onClick={() => setEditModal({ caseId: c.caseId, caseIdVal: c.caseId, notesVal: c.notes || '' })}
                          className="p-1.5 rounded-lg text-gray-300 hover:text-blue-500 hover:bg-blue-50 transition-colors"
                          title="Edit case ID or notes"
                        >
                          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.75}
                              d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                          </svg>
                        </button>
                        <button
                          onClick={() => handleExpand(c.caseId)}
                          className="p-1.5 rounded-lg text-gray-400 hover:bg-gray-100 transition-colors"
                          title="Show uploads"
                        >
                          <svg
                            className={`w-4 h-4 transition-transform duration-200 ${expandedId === c.caseId ? 'rotate-180' : ''}`}
                            fill="none" viewBox="0 0 24 24" stroke="currentColor"
                          >
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                          </svg>
                        </button>
                        {/* Delete icon (admin only) */}
                        {isAdmin && <button
                          onClick={() => setConfirmModal({ caseIds: [c.caseId] })}
                          className="p-1.5 rounded-lg text-gray-300 hover:text-red-500 hover:bg-red-50 transition-colors"
                          title="Delete case"
                        >
                          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.75}
                              d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                          </svg>
                        </button>}
                      </div>
                    </td>
                  </tr>

                  {/* Uploads & Models expansion */}
                  {expandedId === c.caseId && (
                    <tr>
                      <td colSpan={9} className="bg-blue-50 border-b border-blue-100 px-8 py-4">
                        <div className="space-y-5">

                          {/* ── DICOM Uploads section ── */}
                          <div>
                            <p className="text-[11px] uppercase tracking-wide text-gray-400 font-semibold mb-2">DICOM Uploads</p>
                            {uLoading === c.caseId ? (
                              <div className="flex justify-center py-2"><Spinner className="w-5 h-5 text-brand-500" /></div>
                            ) : !uploadsMap[c.caseId]?.length ? (
                              <p className="text-xs text-gray-400 italic">No DICOM uploads yet.</p>
                            ) : (() => {
                              // Group uploads by sessionFolder (or 'standalone' for individual uploads)
                              const uploads = uploadsMap[c.caseId]
                              const groups = {}
                              for (const u of uploads) {
                                const key = u.sessionFolder || `standalone-${u.id}`
                                if (!groups[key]) groups[key] = { sessionFolder: u.sessionFolder, driveFolderId: null, uploads: [], totalSize: 0 }
                                if (u.sessionDriveFolderId) groups[key].driveFolderId = u.sessionDriveFolderId
                                groups[key].uploads.push(u)
                                groups[key].totalSize += Number(u.fileSizeBytes)
                              }
                              return (
                                <div className="space-y-2">
                                  {Object.entries(groups).map(([key, group]) => {
                                    const isSession = !!group.sessionFolder
                                    const fileCount = group.uploads.length
                                    const earliest = group.uploads[group.uploads.length - 1]
                                    const isDeleting = deletingSession === key
                                    // For standalone ZIP uploads, check if validatable
                                    const singleUpload = !isSession && group.uploads[0]
                                    const isValidatable = singleUpload
                                      && singleUpload.validationStatus === 'PENDING'
                                      && singleUpload.driveFileId
                                      && singleUpload.filename?.toLowerCase().endsWith('.zip')
                                    const isValidating = singleUpload && validatingUploadId === singleUpload.id
                                    // Get validation report from any upload in the group
                                    const reportUpload = group.uploads.find(u => u.validationReportJson)
                                    const report = reportUpload?.validationReportJson
                                    const valStatus = reportUpload?.validationReportJson?.status
                                    const sessionKey = `${c.caseId}/${key}`
                                    const isExpanded = expandedSession === sessionKey
                                    return (
                                      <div key={key} className="bg-white rounded-lg border border-gray-100 overflow-hidden">
                                        <div className="flex items-center gap-3 text-xs px-3 py-2">
                                          {/* Folder/archive icon — clickable if report exists */}
                                          <button
                                            onClick={() => report && setExpandedSession(isExpanded ? null : sessionKey)}
                                            className={`flex-shrink-0 w-7 h-7 rounded-full flex items-center justify-center transition-colors ${report ? 'bg-blue-50 hover:bg-blue-100 cursor-pointer' : 'bg-blue-50 cursor-default'}`}
                                            title={report ? (isExpanded ? 'Hide validation report' : 'Show validation report') : ''}
                                          >
                                            <svg className="w-3.5 h-3.5 text-blue-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5}
                                                d={isSession
                                                  ? 'M3 7a2 2 0 012-2h4l2 2h8a2 2 0 012 2v8a2 2 0 01-2 2H5a2 2 0 01-2-2V7z'
                                                  : 'M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4'} />
                                            </svg>
                                          </button>
                                          {/* Label */}
                                          <div className="flex-1 min-w-0">
                                            <button
                                              onClick={() => report && setExpandedSession(isExpanded ? null : sessionKey)}
                                              className={`font-mono text-gray-700 truncate block text-left ${report ? 'hover:text-blue-600 cursor-pointer' : 'cursor-default'}`}
                                            >
                                              {isSession
                                                ? `${group.sessionFolder} (${fileCount} file${fileCount !== 1 ? 's' : ''})`
                                                : singleUpload.filename}
                                            </button>
                                          </div>
                                          {/* Validation status pill — for sessions too */}
                                          {valStatus && (
                                            <span className={`flex-shrink-0 px-1.5 py-0.5 rounded-full font-medium ${VAL_PILL[valStatus] || VAL_PILL.PENDING}`}>
                                              {valStatus}
                                            </span>
                                          )}
                                          {singleUpload && !valStatus && (
                                            <span className={`flex-shrink-0 px-1.5 py-0.5 rounded-full font-medium ${VAL_PILL[singleUpload.validationStatus] || VAL_PILL.PENDING}`}>
                                              {singleUpload.validationStatus}
                                            </span>
                                          )}
                                          {/* Size + date */}
                                          <span className="text-gray-400 flex-shrink-0">{formatBytes(group.totalSize)}</span>
                                          <span className="text-gray-400 flex-shrink-0 whitespace-nowrap">{formatDate(earliest.uploadedAt)}</span>
                                          {/* Expand/collapse chevron if report exists */}
                                          {report && (
                                            <button
                                              onClick={() => setExpandedSession(isExpanded ? null : sessionKey)}
                                              className="flex-shrink-0 p-1 rounded text-gray-300 hover:text-blue-500 transition-colors"
                                              title={isExpanded ? 'Hide report' : 'Show report'}
                                            >
                                              <svg className={`w-3.5 h-3.5 transition-transform ${isExpanded ? 'rotate-180' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                                              </svg>
                                            </button>
                                          )}
                                          {/* Validate button (ZIP only) */}
                                          {isValidatable && (
                                            <button
                                              onClick={() => handleValidateUpload(c.caseId, singleUpload.id)}
                                              disabled={isValidating}
                                              title="Check DICOM magic bytes inside this ZIP"
                                              className="flex-shrink-0 flex items-center gap-1 px-2 py-0.5 rounded-full bg-indigo-100 text-indigo-700 hover:bg-indigo-200 font-medium disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                                            >
                                              {isValidating ? (
                                                <><Spinner className="w-3 h-3" /> Validating…</>
                                              ) : 'Validate'}
                                            </button>
                                          )}
                                          {/* Quality check button — for sessions with a Drive folder */}
                                          {group.driveFolderId && (
                                            <button
                                              onClick={() => handleQualityCheck(c.caseId, key, group.driveFolderId)}
                                              disabled={qualityLoading === key}
                                              title="Check DICOM quality against imaging requirements"
                                              className="flex-shrink-0 flex items-center gap-1 px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700 hover:bg-emerald-200 font-medium disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                                            >
                                              {qualityLoading === key ? (
                                                <><Spinner className="w-3 h-3" /> Checking…</>
                                              ) : 'Quality'}
                                            </button>
                                          )}
                                          {/* DICOM Metadata button — for session folders */}
                                          {group.driveFolderId && (() => {
                                            const representativeId = group.uploads[0]?.id
                                            const dm = dicomMetaMap[c.caseId]
                                            const isActive = dm?.uploadId === representativeId && dm?.open
                                            return (
                                              <button
                                                onClick={() => {
                                                  if (isActive) {
                                                    setDicomMetaMap(prev => ({ ...prev, [c.caseId]: { ...prev[c.caseId], open: false } }))
                                                  } else {
                                                    setDicomMetaMap(prev => ({ ...prev, [c.caseId]: { uploadId: representativeId, open: true, loading: true, data: null, error: null } }))
                                                    getCaseDicomMeta(c.caseId, representativeId)
                                                      .then(r => setDicomMetaMap(prev => ({ ...prev, [c.caseId]: { ...prev[c.caseId], loading: false, data: r.data.meta, error: r.data.error || null } })))
                                                      .catch(() => setDicomMetaMap(prev => ({ ...prev, [c.caseId]: { ...prev[c.caseId], loading: false, data: null, error: 'Failed to load DICOM metadata' } })))
                                                  }
                                                }}
                                                title="Read DICOM metadata from this upload"
                                                className={`flex-shrink-0 flex items-center gap-1 px-2 py-0.5 rounded-full font-medium transition-colors ${isActive ? 'bg-violet-200 text-violet-800' : 'bg-violet-100 text-violet-700 hover:bg-violet-200'}`}
                                              >
                                                <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                                                </svg>
                                                {isActive ? 'Metadata ▾' : 'Metadata'}
                                              </button>
                                            )
                                          })()}
                                          {/* Delete button (admin only) */}
                                          {isAdmin && <button
                                            onClick={() => handleDeleteUploadSession(
                                              c.caseId,
                                              isSession ? group.sessionFolder : null,
                                              isSession ? null : singleUpload.id,
                                            )}
                                            disabled={isDeleting}
                                            title={isSession ? 'Delete this upload session and all files' : 'Delete this upload'}
                                            className="flex-shrink-0 p-1 rounded text-gray-300 hover:text-red-500 hover:bg-red-50 transition-colors disabled:opacity-50"
                                          >
                                            {isDeleting
                                              ? <Spinner className="w-3.5 h-3.5" />
                                              : <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.75}
                                                    d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                                </svg>
                                            }
                                          </button>}
                                        </div>
                                        {/* Expanded report (validation or quality) */}
                                        {isExpanded && (() => {
                                          const qd = qualityData[key]
                                          const qChecks = qd?.quality?.checks
                                          const showQuality = qChecks && qChecks.length > 0
                                          const showQualityError = !!qd?.error && !showQuality
                                          const showValidation = report && !qChecks && !showQualityError
                                          if (!showValidation && !showQuality && !showQualityError) return null
                                          return (
                                            <div className="px-3 pb-3 pt-1 border-t border-gray-50">
                                              {/* Quality error (e.g. unsupported .rar archive) */}
                                              {showQualityError && (
                                                <div className="text-[11px] text-red-500 italic">{qd.error}</div>
                                              )}
                                              {/* Quality report header */}
                                              {showQuality && (
                                                <div className="flex items-center gap-2 mb-2">
                                                  <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold text-white ${
                                                    qd.quality.printabilityGrade === 'Excellent' ? 'bg-green-500' :
                                                    qd.quality.printabilityGrade === 'Good' ? 'bg-blue-500' :
                                                    qd.quality.printabilityGrade === 'Fair' ? 'bg-yellow-500' : 'bg-red-500'
                                                  }`}>{qd.quality.printabilityGrade}</span>
                                                  <span className="text-[11px] text-gray-500">
                                                    Score: {qd.quality.printabilityScore}/{qd.quality.printabilityMaxScore}
                                                  </span>
                                                  {qd.quality.seriesType && qd.quality.seriesType !== 'Unknown' && (
                                                    <span className="text-[11px] text-gray-500">| {qd.quality.seriesType}</span>
                                                  )}
                                                  {qd.quality.manufacturer && (
                                                    <span className="text-[11px] text-gray-400">| {qd.quality.manufacturer}</span>
                                                  )}
                                                  {qd.sliceCount > 0 && (
                                                    <span className="text-[11px] text-gray-400">| {qd.sliceCount} slices</span>
                                                  )}
                                                </div>
                                              )}
                                              {/* Quality checks */}
                                              {showQuality && (
                                                <div className="space-y-1">
                                                  {qChecks.map((chk, ci) => (
                                                    <div key={ci} className="flex items-start gap-2 text-[11px]">
                                                      <span className={`flex-shrink-0 mt-0.5 w-4 h-4 rounded-full flex items-center justify-center text-white text-[9px] font-bold ${
                                                        chk.status === 'PASSED' ? 'bg-green-500' : chk.status === 'WARNING' ? 'bg-yellow-500' : 'bg-red-500'
                                                      }`}>
                                                        {chk.status === 'PASSED' ? '\u2713' : chk.status === 'WARNING' ? '!' : '\u2717'}
                                                      </span>
                                                      <span className="font-medium text-gray-600 w-28 flex-shrink-0">{chk.name}</span>
                                                      <span className="text-gray-500 flex-1">{chk.message}</span>
                                                      <span className="text-gray-400 flex-shrink-0 text-right">{chk.value}</span>
                                                    </div>
                                                  ))}
                                                </div>
                                              )}
                                              {/* Quality error */}
                                              {qd?.error && !showQuality && (
                                                <p className="text-[11px] text-red-500">{qd.error}</p>
                                              )}
                                              {/* Fallback: validation report (old format) */}
                                              {showValidation && (
                                                <>
                                                  {report.meta && (
                                                    <div className="flex flex-wrap gap-x-4 gap-y-1 text-[11px] text-gray-500 mb-2">
                                                      {report.meta.seriesDescription && <span>Series: <span className="text-gray-700 font-medium">{report.meta.seriesDescription}</span></span>}
                                                      {report.meta.studyDate && <span>Study: <span className="text-gray-700 font-medium">{report.meta.studyDate}</span></span>}
                                                      {report.meta.patientName && <span>Patient: <span className="text-gray-700 font-medium">{report.meta.patientName}</span></span>}
                                                    </div>
                                                  )}
                                                  {report.checks?.length > 0 && (
                                                    <div className="space-y-1">
                                                      {report.checks.map((chk, ci) => (
                                                        <div key={ci} className="flex items-start gap-2 text-[11px]">
                                                          <span className={`flex-shrink-0 mt-0.5 w-4 h-4 rounded-full flex items-center justify-center text-white text-[9px] font-bold ${
                                                            chk.status === 'PASSED' ? 'bg-green-500' : chk.status === 'WARNING' ? 'bg-yellow-500' : 'bg-red-500'
                                                          }`}>
                                                            {chk.status === 'PASSED' ? '\u2713' : chk.status === 'WARNING' ? '!' : '\u2717'}
                                                          </span>
                                                          <span className="font-medium text-gray-600 w-28 flex-shrink-0">{chk.name}</span>
                                                          <span className="text-gray-500 flex-1">{chk.message}</span>
                                                          <span className="text-gray-400 flex-shrink-0 text-right">{chk.value}</span>
                                                        </div>
                                                      ))}
                                                    </div>
                                                  )}
                                                </>
                                              )}
                                            </div>
                                          )
                                        })()}
                                      </div>
                                    )
                                  })}
                                </div>
                              )
                            })()}
                          </div>

                          {/* ── 3D Models section ── */}
                          <div>
                            <div className="flex items-center justify-between mb-2">
                              <p className="text-[11px] uppercase tracking-wide text-gray-400 font-semibold">3D Models (GLB / STL)</p>
                              <label className="text-xs px-3 py-1.5 rounded-lg bg-brand-600 text-white hover:bg-brand-700 transition-colors cursor-pointer font-medium">
                                Upload Model
                                <input
                                  ref={modelFileRef}
                                  type="file"
                                  // .json admits the SSE `<stl>.sse-meta.json` provenance
                                  // sidecar; multiple lets the operator select the model
                                  // and its sidecar together (gate M2).
                                  accept=".glb,.stl,.json"
                                  multiple
                                  className="hidden"
                                  disabled={modelUploading === c.caseId}
                                  onChange={(e) => {
                                    if (e.target.files?.length) handleModelUpload(c.caseId, e.target.files)
                                  }}
                                />
                              </label>
                            </div>

                            {/* Upload progress */}
                            {modelUploading === c.caseId && (
                              <div className="mb-3">
                                <div className="flex items-center gap-2 text-xs text-brand-700 mb-1">
                                  <Spinner className="w-3 h-3" />
                                  <span>{modelUploadPct >= 100 ? 'Converting STL → GLB & uploading to Drive…' : `Uploading… ${modelUploadPct}%`}</span>
                                </div>
                                <div className="w-full bg-gray-200 rounded-full h-1.5">
                                  <div
                                    className="bg-brand-600 h-1.5 rounded-full transition-all duration-300"
                                    style={{ width: `${modelUploadPct}%` }}
                                  />
                                </div>
                              </div>
                            )}

                            {modelErr && expandedId === c.caseId && (
                              <p className="text-xs text-red-500 mb-2">{modelErr}</p>
                            )}

                            {mLoading === c.caseId ? (
                              <div className="flex justify-center py-2"><Spinner className="w-5 h-5 text-brand-500" /></div>
                            ) : !modelsMap[c.caseId]?.length ? (
                              <p className="text-xs text-gray-400 italic">No 3D models yet. Upload a .glb or .stl file above.</p>
                            ) : (
                              <div className="space-y-2">
                                {modelsMap[c.caseId].map((m) => (
                                  <div key={m.id} className="flex items-center gap-3 text-xs bg-white rounded-lg px-3 py-2 border border-gray-100">
                                    <span className="flex-shrink-0 px-1.5 py-0.5 rounded-full font-medium bg-purple-100 text-purple-700">
                                      v{m.version}
                                    </span>
                                    <span className="font-mono text-gray-700 truncate flex-1" title={m.filename}>
                                      {m.glbFilename || m.filename}
                                    </span>
                                    {m.description && (
                                      <span className="text-gray-400 truncate max-w-[120px]" title={m.description}>{m.description}</span>
                                    )}
                                    <span className="text-gray-400 flex-shrink-0">{formatBytes(m.fileSizeBytes)}</span>
                                    <span className="text-gray-400 flex-shrink-0 whitespace-nowrap">{formatDate(m.createdAt)}</span>
                                    <a
                                      href={import.meta.env.DEV
                                        ? `http://localhost:5174/view/${c.caseId}/${m.id}`
                                        : `https://viewer.osteo3d.com/view/${c.caseId}/${m.id}`}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      title="View 3D model"
                                      className="flex-shrink-0 flex items-center gap-1 px-2 py-0.5 rounded-full bg-purple-100 text-purple-700 hover:bg-purple-200 font-medium transition-colors"
                                    >
                                      <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                                      </svg>
                                      View
                                    </a>
                                    {isAdmin && <button
                                      onClick={() => handleDeleteModel(c.caseId, m.id)}
                                      disabled={deletingModel === m.id}
                                      className="flex-shrink-0 p-1 rounded text-gray-300 hover:text-red-500 hover:bg-red-50 transition-colors disabled:opacity-50"
                                      title="Delete model"
                                    >
                                      {deletingModel === m.id ? (
                                        <Spinner className="w-3.5 h-3.5" />
                                      ) : (
                                        <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                                            d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                        </svg>
                                      )}
                                    </button>}
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>

                          {/* ── DICOM Metadata ── */}
                          {(() => {
                            const dm = dicomMetaMap[c.caseId] || {}
                            const DICOM_FIELDS = [
                              ['Scan Type',             dm.data?.scanType],
                              ['Slice Thickness',        dm.data?.sliceThickness],
                              ['Spacing Between Slices', dm.data?.spacingBetweenSlices],
                              ['Scan Region',            dm.data?.scanRegion],
                              ['Contrast',               dm.data?.contrast],
                              ['Patient Age',            dm.data?.patientAge],
                              ['Study Date',             dm.data?.studyDate],
                              ['DICOM Received Date',    dm.data?.dicomReceivedDate],
                              ['Series No',              dm.data?.seriesNo],
                              ['Series Name',            dm.data?.seriesName],
                              ['Series Type',            dm.data?.seriesType],
                              ['Manufacturer',           dm.data?.manufacturer],
                              ['Convolution Kernel',     dm.data?.convolutionKernel],
                              ['Window Width',           dm.data?.windowWidth],
                              ['File Name',              dm.data?.fileName],
                              ['Min Threshold',          '(enter manually)'],
                              ['Max Threshold',          '(enter manually)'],
                              ['DICOM File Name',        dm.data?.dicomFileName],
                              ['Institution Name',       dm.data?.institutionName],
                              ['Institution Address',    dm.data?.institutionAddress],
                              ['Referring Physician',    dm.data?.referringPhysician],
                            ]
                            function closeDicomMeta() {
                              setDicomMetaMap(prev => ({ ...prev, [c.caseId]: { ...prev[c.caseId], open: false } }))
                            }
                            function copyDicomMeta() {
                              const lines = DICOM_FIELDS.map(([label, val]) => `${label}: ${val || '—'}`).join('\n')
                              navigator.clipboard.writeText(lines).then(() => {
                                setDicomMetaMap(prev => ({ ...prev, [c.caseId]: { ...prev[c.caseId], copied: true } }))
                                setTimeout(() => setDicomMetaMap(prev => ({ ...prev, [c.caseId]: { ...prev[c.caseId], copied: false } })), 2000)
                              })
                            }
                            return (
                              <div>
                                <div className="flex items-center gap-2 mb-2">
                                  <p className="text-[11px] uppercase tracking-wide text-gray-400 font-semibold">DICOM Metadata</p>
                                  {dm.open && (
                                    <button onClick={closeDicomMeta} className="text-gray-300 hover:text-gray-500 transition-colors" title="Close">
                                      <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                      </svg>
                                    </button>
                                  )}
                                </div>
                                {dm.open && (
                                  <div className="bg-white border border-gray-100 rounded-lg px-4 py-3">
                                    {dm.loading ? (
                                      <div className="flex justify-center py-3"><Spinner className="w-4 h-4 text-brand-500" /></div>
                                    ) : dm.error ? (
                                      <p className="text-xs text-red-400 italic">{dm.error}</p>
                                    ) : (
                                      <>
                                        <div className="flex justify-end mb-2">
                                          <button
                                            onClick={copyDicomMeta}
                                            className="flex items-center gap-1 px-2.5 py-1 rounded text-xs border border-gray-200 text-gray-500 hover:bg-gray-50 transition-colors"
                                          >
                                            {dm.copied ? (
                                              <><svg className="w-3.5 h-3.5 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" /></svg> Copied!</>
                                            ) : (
                                              <><svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" /></svg> Copy</>
                                            )}
                                          </button>
                                        </div>
                                        {dm.data?.spacingWarning && (
                                          <div className="mb-2 flex items-start gap-1.5 rounded-md bg-amber-50 border border-amber-200 px-2.5 py-1.5 text-[11px] text-amber-700">
                                            <svg className="w-3.5 h-3.5 flex-shrink-0 mt-px" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" /></svg>
                                            <span>{dm.data.spacingWarning}</span>
                                          </div>
                                        )}
                                        <div className="grid grid-cols-2 gap-x-6 gap-y-1">
                                          {DICOM_FIELDS.map(([label, val]) => (
                                            <div key={label} className="flex items-baseline gap-1 text-xs py-0.5 border-b border-gray-50">
                                              <span className="text-gray-400 flex-shrink-0 w-[140px]">{label}</span>
                                              <span className={`text-gray-700 truncate ${!val || val === '(enter manually)' ? 'text-gray-300 italic' : ''}`}>
                                                {val || '—'}
                                              </span>
                                            </div>
                                          ))}
                                        </div>
                                      </>
                                    )}
                                  </div>
                                )}
                              </div>
                            )
                          })()}

                          {/* ── Surgeon Markings Timeline ── */}
                          <div>
                            <p className="text-[11px] uppercase tracking-wide text-gray-400 font-semibold mb-2">Surgeon Markings</p>
                            {aLoading === c.caseId ? (
                              <div className="flex justify-center py-2"><Spinner className="w-5 h-5 text-brand-500" /></div>
                            ) : !annotationsMap[c.caseId]?.length ? (
                              <p className="text-xs text-gray-400 italic">No surgeon markings yet.</p>
                            ) : (
                              <div className="space-y-2">
                                {annotationsMap[c.caseId].map((ann) => (
                                  <div key={ann.id} className="flex items-start gap-3 text-xs bg-white rounded-lg px-3 py-2 border border-gray-100">
                                    <div className="flex-shrink-0 mt-0.5">
                                      {ann.status === 'APPROVED' ? (
                                        <span className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-green-100">
                                          <svg className="w-3 h-3 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" /></svg>
                                        </span>
                                      ) : ann.status === 'AWAITING_REVIEW' ? (
                                        <span className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-amber-100 text-amber-600 text-[9px] font-bold">!</span>
                                      ) : ann.status === 'IN_REVISION' ? (
                                        <span className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-blue-100 text-blue-600 text-[9px] font-bold">R</span>
                                      ) : (
                                        <span className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-orange-100 text-orange-600 text-[9px] font-bold">S</span>
                                      )}
                                    </div>
                                    <div className="flex-1 min-w-0">
                                      <div className="flex items-center gap-2">
                                        <span className="font-medium text-gray-700">{ann.authorName}</span>
                                        <span className={`text-[9px] font-semibold uppercase px-1.5 py-0.5 rounded-full ${
                                          ann.status === 'APPROVED' ? 'bg-green-100 text-green-700'
                                            : ann.status === 'AWAITING_REVIEW' ? 'bg-amber-100 text-amber-700'
                                            : ann.status === 'IN_REVISION' ? 'bg-blue-100 text-blue-700'
                                            : 'bg-orange-100 text-orange-700'
                                        }`}>
                                          {ann.status === 'AWAITING_REVIEW' ? 'Awaiting Review'
                                            : ann.status === 'IN_REVISION' ? 'In Revision'
                                            : ann.status}
                                        </span>
                                        <span className="text-gray-400 ml-auto flex-shrink-0">{formatDate(ann.createdAt)}</span>
                                      </div>
                                      {ann.notes && <p className="text-gray-500 mt-0.5 line-clamp-2">{ann.notes}</p>}
                                      {ann.pinLabels?.length > 0 && (
                                        <p className="text-gray-400 mt-0.5">{ann.pinLabels.length} pin(s)</p>
                                      )}
                                    </div>
                                    {ann.status === 'SUBMITTED' && (
                                      <button
                                        onClick={async () => {
                                          setRequestingReview(ann.id)
                                          try {
                                            await requestReview(c.caseId, { annotationId: ann.id })
                                            setAnnotationsMap((prev) => ({
                                              ...prev,
                                              [c.caseId]: prev[c.caseId].map((a) =>
                                                a.id === ann.id ? { ...a, status: 'AWAITING_REVIEW' } : a
                                              ),
                                            }))
                                          } catch (_) {}
                                          setRequestingReview(null)
                                        }}
                                        disabled={requestingReview === ann.id}
                                        className="flex-shrink-0 text-[10px] px-2 py-1 rounded bg-amber-50 text-amber-700 hover:bg-amber-100 transition disabled:opacity-50"
                                      >
                                        {requestingReview === ann.id ? '…' : 'Request Review'}
                                      </button>
                                    )}
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>

                        </div>
                      </td>
                    </tr>
                  )}

                </Fragment>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Pagination */}
      {pages > 1 && (
        <div className="flex items-center justify-between text-sm text-gray-500">
          <span>{total} cases total</span>
          <div className="flex items-center gap-2">
            <button
              onClick={() => changePage(page - 1)}
              disabled={page === 1}
              className="px-3 py-1.5 rounded-lg border border-gray-200 text-sm disabled:opacity-40 hover:bg-gray-50 transition-colors"
            >
              ← Prev
            </button>
            <span className="text-sm">Page {page} of {pages}</span>
            <button
              onClick={() => changePage(page + 1)}
              disabled={page === pages}
              className="px-3 py-1.5 rounded-lg border border-gray-200 text-sm disabled:opacity-40 hover:bg-gray-50 transition-colors"
            >
              Next →
            </button>
          </div>
        </div>
      )}

      {/* Meet modal */}
      {meetModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-md mx-4 p-6">
            <h3 className="text-sm font-semibold text-gray-800 mb-1">Schedule Google Meet</h3>
            <p className="text-xs text-gray-400 mb-4">Case {meetModal.caseId}</p>

            {/* Care team attendees */}
            <div className="mb-4">
              <p className="text-xs font-medium text-gray-600 mb-2">Invitees (care team)</p>
              {meetModal.contacts.length === 0 ? (
                <p className="text-xs text-gray-400 italic">No care team contacts — only Osteo3d team will be invited</p>
              ) : (
                <div className="space-y-1 max-h-32 overflow-y-auto">
                  {meetModal.contacts.map(ct => (
                    <div key={ct.id} className="flex items-center gap-2 text-xs text-gray-600">
                      <span className="w-1.5 h-1.5 bg-green-400 rounded-full flex-shrink-0" />
                      <span className="font-medium">{ct.name}</span>
                      <span className="text-gray-400">{ct.email}</span>
                    </div>
                  ))}
                </div>
              )}
              <p className="text-[11px] text-gray-400 mt-2">All Osteo3d team members will be CC'd</p>
            </div>

            {/* Invite additional doctors */}
            <div className="mb-4">
              <label className="text-xs font-medium text-gray-600 block mb-1">Invite doctor(s) by email</label>
              <textarea
                value={meetDoctorEmails}
                onChange={e => setMeetDoctorEmails(e.target.value)}
                placeholder="doctor1@example.com, doctor2@example.com"
                rows={2}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-300 resize-none"
              />
              <p className="text-[11px] text-gray-400 mt-1">Separate multiple with comma or space. They'll be added to the Calendar invite and saved to this case's care team.</p>
            </div>

            {/* Date / time / duration */}
            <div className="grid grid-cols-2 gap-3 mb-4">
              <div>
                <label className="text-xs text-gray-500 block mb-1">Date (optional)</label>
                <input
                  type="date"
                  value={meetDate}
                  onChange={e => setMeetDate(e.target.value)}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-300"
                />
              </div>
              <div>
                <label className="text-xs text-gray-500 block mb-1">Time</label>
                <input
                  type="time"
                  value={meetTime}
                  onChange={e => setMeetTime(e.target.value)}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-300"
                />
              </div>
            </div>
            <div className="mb-5">
              <label className="text-xs text-gray-500 block mb-1">Duration</label>
              <select
                value={meetDuration}
                onChange={e => setMeetDuration(Number(e.target.value))}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-300"
              >
                <option value={30}>30 minutes</option>
                <option value={60}>60 minutes</option>
                <option value={90}>90 minutes</option>
                <option value={120}>2 hours</option>
              </select>
            </div>
            <div className="mb-4">
              <label className="text-xs text-gray-500 block mb-1">Notes / Agenda (optional)</label>
              <textarea
                value={meetNotes}
                onChange={e => setMeetNotes(e.target.value)}
                placeholder="Discussion topics, preparation notes…"
                rows={2}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-300 resize-none"
              />
            </div>

            {meetError && (
              <div className="mb-4">
                {meetError === 'CALENDAR_NOT_CONNECTED' ? (
                  <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
                    <p className="text-xs text-amber-800 font-medium mb-2">Calendar not connected</p>
                    <p className="text-xs text-amber-600 mb-3">An admin must connect the planning calendar account (contactus@osteo3d.com) once to enable Meet invites.</p>
                    <a
                      href={`${import.meta.env.VITE_API_BASE_URL || ''}/api/admin/auth/calendar?redirect=${encodeURIComponent(window.location.href)}`}
                      className="inline-block text-xs px-3 py-1.5 rounded-lg bg-amber-600 text-white hover:bg-amber-700 transition-colors font-medium"
                    >
                      Connect Calendar Account
                    </a>
                  </div>
                ) : (
                  <p className="text-xs text-red-500">{meetError}</p>
                )}
              </div>
            )}

            <div className="flex gap-2 justify-end">
              <button
                onClick={() => setMeetModal(null)}
                disabled={meetCreating}
                className="text-sm px-4 py-2 rounded-lg border border-gray-200 text-gray-600 hover:bg-gray-50 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleCreateMeet}
                disabled={meetCreating}
                className="text-sm px-4 py-2 rounded-lg bg-teal-600 text-white hover:bg-teal-700 transition-colors disabled:opacity-60 flex items-center gap-2"
              >
                {meetCreating && (
                  <svg className="w-3.5 h-3.5 animate-spin" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
                  </svg>
                )}
                {meetCreating ? 'Creating…' : 'Create Meet'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

// ── Confirmation modal for case deletion ─────────────────────────
function ConfirmDeleteModal({ caseIds, onConfirm, onCancel, loading }) {
  const isBulk = caseIds.length > 1
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4">
      <div className="bg-white rounded-2xl shadow-xl max-w-md w-full p-6">

        {/* Header */}
        <div className="flex items-start gap-3 mb-4">
          <div className="flex-shrink-0 w-10 h-10 rounded-full bg-red-100 flex items-center justify-center">
            <svg className="w-5 h-5 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
          </div>
          <div>
            <h3 className="text-base font-semibold text-gray-900">
              {isBulk ? `Delete ${caseIds.length} Cases?` : `Delete ${caseIds[0]}?`}
            </h3>
            <p className="text-sm text-gray-500 mt-1">
              This permanently removes the case record{isBulk ? 's' : ''} and all associated
              upload records from the portal database. Files already on Google Drive are
              not affected.
            </p>
          </div>
        </div>

        {/* Case list (bulk only) */}
        {isBulk && (
          <div className="mb-4 bg-gray-50 rounded-lg px-3 py-2.5 max-h-32 overflow-y-auto">
            <p className="text-xs text-gray-400 mb-1.5">Cases to be deleted:</p>
            <div className="flex flex-wrap gap-1.5">
              {caseIds.map((id) => (
                <span key={id} className="text-xs font-mono bg-red-50 text-red-700 border border-red-200 px-1.5 py-0.5 rounded">
                  {id}
                </span>
              ))}
            </div>
          </div>
        )}

        {/* Warning */}
        <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 mb-5">
          This action cannot be undone.
        </p>

        {/* Buttons */}
        <div className="flex items-center justify-end gap-3">
          <button
            onClick={onCancel}
            disabled={loading}
            className="px-4 py-2 rounded-lg border border-gray-200 text-sm text-gray-600 hover:bg-gray-50 transition-colors disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            onClick={onConfirm}
            disabled={loading}
            className="px-4 py-2 rounded-lg bg-red-600 text-white text-sm font-medium hover:bg-red-700 transition-colors disabled:opacity-50 flex items-center gap-2"
          >
            {loading && <Spinner className="w-3.5 h-3.5" />}
            {loading ? 'Deleting…' : `Delete ${isBulk ? `${caseIds.length} cases` : 'case'}`}
          </button>
        </div>
      </div>
    </div>
  )
}

// ═════════════════════════════════════════════════════════════════
// Markings tab (surgeon annotations on 3D models)
// ═════════════════════════════════════════════════════════════════
const MARK_PILL = {
  SUBMITTED:       'bg-orange-100 text-orange-700',
  IN_REVISION:     'bg-blue-100 text-blue-700',
  AWAITING_REVIEW: 'bg-amber-100 text-amber-700',
  APPROVED:        'bg-green-100 text-green-700',
}

const MARK_LABEL = {
  SUBMITTED:       'Submitted',
  IN_REVISION:     'In Revision',
  AWAITING_REVIEW: 'Awaiting Review',
  APPROVED:        'Approved',
}

function MarkingsTab({ role }) {
  const isAdmin = role === 'admin'
  const [annotations, setAnnotations] = useState([])
  const [loading, setLoading]         = useState(true)
  const [total, setTotal]             = useState(0)
  const [page, setPage]               = useState(1)
  const [pages, setPages]             = useState(1)
  const [filterStatus, setFilterStatus] = useState('')
  const [updating, setUpdating]       = useState(null)
  const [previewId, setPreviewId]     = useState(null)
  const [requesting, setRequesting]   = useState(null)
  const [requestMsg, setRequestMsg]   = useState({})

  const load = useCallback((pg = 1) => {
    setLoading(true)
    const params = { page: pg, limit: 20 }
    if (filterStatus) params.status = filterStatus
    getAnnotations(params)
      .then((res) => {
        setAnnotations(res.data.annotations)
        setTotal(res.data.total)
        setPage(res.data.page)
        setPages(res.data.pages)
      })
      .catch(() => {})
      .finally(() => setLoading(false))
  }, [filterStatus])

  useEffect(() => { load(1) }, [load])

  async function handleStatus(id, status) {
    setUpdating(id)
    try {
      await updateAnnotationStatus(id, status)
      setAnnotations((prev) =>
        prev.map((a) => (a.id === id ? { ...a, status } : a))
      )
    } catch (_) {}
    setUpdating(null)
  }

  async function handleRequestReview(annotation) {
    setRequesting(annotation.id)
    try {
      await requestReview(annotation.caseId, { annotationId: annotation.id })
      setAnnotations((prev) =>
        prev.map((a) => (a.id === annotation.id ? { ...a, status: 'AWAITING_REVIEW' } : a))
      )
      setRequestMsg((prev) => ({ ...prev, [annotation.id]: 'Review request sent!' }))
      setTimeout(() => setRequestMsg((prev) => ({ ...prev, [annotation.id]: null })), 3000)
    } catch (err) {
      setRequestMsg((prev) => ({ ...prev, [annotation.id]: err.response?.data?.error || 'Failed to send' }))
    }
    setRequesting(null)
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold text-gray-800">Surgeon Markings</h2>
          <p className="text-xs text-gray-400 mt-0.5">Review cycle: Submitted → In Revision → Awaiting Review → Approved</p>
        </div>
        <div className="flex items-center gap-3">
          <select
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
            className="text-sm border border-gray-200 rounded-lg px-3 py-1.5 text-gray-600"
          >
            <option value="">All statuses</option>
            <option value="SUBMITTED">Submitted</option>
            <option value="IN_REVISION">In Revision</option>
            <option value="AWAITING_REVIEW">Awaiting Review</option>
            <option value="APPROVED">Approved</option>
          </select>
          <button onClick={() => load(page)} className="text-sm text-brand-600 hover:text-brand-800">
            Refresh
          </button>
        </div>
      </div>

      {loading ? (
        <div className="flex justify-center py-12"><Spinner className="w-6 h-6 text-brand-500" /></div>
      ) : !annotations.length ? (
        <div className="text-center py-12 text-gray-400">
          <p className="text-lg mb-1">No markings yet</p>
          <p className="text-sm">When surgeons annotate 3D models from the viewer, their markings will appear here.</p>
        </div>
      ) : (
        <>
          <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-gray-50 text-gray-500 text-xs uppercase tracking-wide">
                  <th className="text-left px-4 py-3 font-medium">Case</th>
                  <th className="text-left px-4 py-3 font-medium">Model</th>
                  <th className="text-left px-4 py-3 font-medium">Surgeon</th>
                  <th className="text-left px-4 py-3 font-medium">Pins</th>
                  <th className="text-left px-4 py-3 font-medium">Notes</th>
                  <th className="text-left px-4 py-3 font-medium">Status</th>
                  <th className="text-left px-4 py-3 font-medium">Date</th>
                  <th className="text-right px-4 py-3 font-medium">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {annotations.map((a) => (
                  <Fragment key={a.id}>
                    <tr className={`hover:bg-gray-50 transition-colors ${a.status === 'SUBMITTED' ? 'bg-orange-50/30' : ''}`}>
                      <td className="px-4 py-3 font-mono text-xs text-gray-700">{a.caseId}</td>
                      <td className="px-4 py-3 text-xs text-gray-600 truncate max-w-[140px]" title={a.model?.glbFilename}>
                        <span>{a.model?.glbFilename || '—'}</span>
                        {a.model?.version && <span className="ml-1 text-purple-500">v{a.model.version}</span>}
                      </td>
                      <td className="px-4 py-3">
                        <span className="text-xs font-medium text-gray-700">{a.authorName}</span>
                        {a.contact && (
                          <span className="text-[10px] text-gray-400 ml-1">({a.contact.role})</span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-xs text-gray-500">
                        {Array.isArray(a.pinLabels) ? a.pinLabels.length : 0}
                      </td>
                      <td className="px-4 py-3 text-xs text-gray-500 truncate max-w-[160px]" title={a.notes || ''}>
                        {a.notes || '—'}
                      </td>
                      <td className="px-4 py-3">
                        <span className={`text-[10px] font-semibold uppercase px-2 py-0.5 rounded-full ${MARK_PILL[a.status] || MARK_PILL.SUBMITTED}`}>
                          {MARK_LABEL[a.status] || a.status}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-xs text-gray-400 whitespace-nowrap">{formatDate(a.createdAt)}</td>
                      <td className="px-4 py-3 text-right">
                        <div className="flex items-center justify-end gap-1 flex-wrap">
                          {/* View 3D — available when annotated GLB exists */}
                          {a.driveFileId && (
                            <button
                              onClick={() => setPreviewId(previewId === a.id ? null : a.id)}
                              className="text-xs px-2 py-1 rounded-lg bg-purple-50 text-purple-700 hover:bg-purple-100 transition-colors"
                              title="View marked model"
                            >
                              View 3D
                            </button>
                          )}
                          {/* Start Revision — when surgeon just submitted (admin only) */}
                          {isAdmin && a.status === 'SUBMITTED' && (
                            <button
                              onClick={() => handleStatus(a.id, 'IN_REVISION')}
                              disabled={updating === a.id}
                              className="text-xs px-2 py-1 rounded-lg bg-blue-50 text-blue-700 hover:bg-blue-100 transition-colors disabled:opacity-50"
                            >
                              {updating === a.id ? '…' : 'Start Revision'}
                            </button>
                          )}
                          {/* Request Review — after team revises model, send to surgeon (admin only) */}
                          {isAdmin && (a.status === 'IN_REVISION' || a.status === 'SUBMITTED') && (
                            <button
                              onClick={() => handleRequestReview(a)}
                              disabled={requesting === a.id}
                              className="text-xs px-2 py-1 rounded-lg bg-amber-50 text-amber-700 hover:bg-amber-100 transition-colors disabled:opacity-50"
                            >
                              {requesting === a.id ? '…' : 'Request Review'}
                            </button>
                          )}
                          {/* Status message */}
                          {requestMsg[a.id] && (
                            <span className="text-[10px] text-green-600">{requestMsg[a.id]}</span>
                          )}
                        </div>
                      </td>
                    </tr>
                    {previewId === a.id && a.driveFileId && (
                      <tr>
                        <td colSpan={8} className="px-4 py-4 bg-gray-50">
                          <div className="rounded-xl overflow-hidden border border-gray-200 bg-black" style={{ height: 400 }}>
                            <model-viewer
                              src={getAnnotationFileUrl(a.id)}
                              alt={`Marked model by ${a.authorName}`}
                              camera-controls
                              touch-action="pan-y"
                              camera-orbit="0deg 90deg auto"
                              interaction-prompt="none"
                              environment-image="legacy"
                              tone-mapping="commerce"
                              exposure="0.6"
                              shadow-intensity="2"
                              style={{ width: '100%', height: '100%' }}
                            />
                          </div>
                          <p className="text-xs text-gray-400 mt-2 text-center">
                            Marked model — {a.authorName} on {a.model?.glbFilename}
                          </p>
                        </td>
                      </tr>
                    )}
                  </Fragment>
                ))}
              </tbody>
            </table>
          </div>

          {pages > 1 && (
            <div className="flex items-center justify-between text-sm text-gray-500">
              <span>{total} marking{total !== 1 ? 's' : ''} total</span>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => load(page - 1)}
                  disabled={page === 1}
                  className="px-3 py-1.5 rounded-lg border border-gray-200 text-sm disabled:opacity-40 hover:bg-gray-50 transition-colors"
                >
                  ← Prev
                </button>
                <span className="text-sm">Page {page} of {pages}</span>
                <button
                  onClick={() => load(page + 1)}
                  disabled={page === pages}
                  className="px-3 py-1.5 rounded-lg border border-gray-200 text-sm disabled:opacity-40 hover:bg-gray-50 transition-colors"
                >
                  Next →
                </button>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  )
}

// ═════════════════════════════════════════════════════════════════
// Recipients tab
// ═════════════════════════════════════════════════════════════════
const BLANK_FORM = { name: '', phoneNumber: '', role: '', email: '' }

function RecipientsTab({ role }) {
  const isAdmin = role === 'admin'
  const [recipients,    setRecipients]    = useState([])
  const [loading,       setLoading]       = useState(true)
  const [form,          setForm]          = useState(BLANK_FORM)
  const [adding,        setAdding]        = useState(false)
  const [addErr,        setAddErr]        = useState(null)
  const [testing,       setTesting]       = useState(null)
  const [testResult,    setTestResult]    = useState({})
  const [removing,      setRemoving]      = useState(null)
  const [deleteConfirm, setDeleteConfirm] = useState(null)   // { id, name } | null
  const [deleting,      setDeleting]      = useState(false)
  const [editModal,     setEditModal]     = useState(null)   // recipient object | null
  const [editSaving,    setEditSaving]    = useState(false)
  const [editErr,       setEditErr]       = useState(null)

  useEffect(() => {
    getRecipients()
      .then((res) => setRecipients(res.data))
      .catch(() => {})
      .finally(() => setLoading(false))
  }, [])

  function setField(field) {
    return (e) => setForm((f) => ({ ...f, [field]: e.target.value }))
  }
  function setEditField(field) {
    return (e) => setEditModal((m) => ({ ...m, [field]: e.target.value }))
  }

  async function handleAdd() {
    if (!form.name.trim() || !form.phoneNumber.trim()) {
      setAddErr('Name and phone number are required.')
      return
    }
    setAdding(true)
    setAddErr(null)
    try {
      const res = await addRecipient({
        name:        form.name.trim(),
        phoneNumber: form.phoneNumber.trim(),
        role:        form.role.trim()  || undefined,
        email:       form.email.trim() || undefined,
      })
      setRecipients((prev) => [...prev, res.data])
      setForm(BLANK_FORM)
    } catch (err) {
      setAddErr(err.response?.data?.error || 'Failed to add recipient.')
    } finally {
      setAdding(false)
    }
  }

  async function handleTest(id) {
    setTesting(id)
    setTestResult((prev) => ({ ...prev, [id]: null }))
    try {
      await sendTestMessage(id)
      setTestResult((prev) => ({ ...prev, [id]: 'ok' }))
    } catch (_) {
      setTestResult((prev) => ({ ...prev, [id]: 'error' }))
    } finally {
      setTesting(null)
      setTimeout(() => setTestResult((prev) => ({ ...prev, [id]: null })), 3000)
    }
  }

  async function handleRemove(id, name) {
    if (!window.confirm(`Deactivate ${name}? They will no longer receive notifications.`)) return
    setRemoving(id)
    try {
      await removeRecipient(id)
      setRecipients((prev) => prev.map((r) => r.id === id ? { ...r, isActive: false } : r))
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to deactivate recipient.')
    } finally {
      setRemoving(null)
    }
  }

  async function handleDeletePermanent() {
    if (!deleteConfirm) return
    setDeleting(true)
    try {
      await deleteRecipientPermanent(deleteConfirm.id)
      setRecipients((prev) => prev.filter((r) => r.id !== deleteConfirm.id))
      setDeleteConfirm(null)
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to delete recipient.')
    } finally {
      setDeleting(false)
    }
  }

  async function handleEditSave() {
    if (!editModal) return
    setEditSaving(true)
    setEditErr(null)
    try {
      const res = await updateRecipient(editModal.id, {
        name:              editModal.name,
        phoneNumber:       editModal.phoneNumber,
        role:              editModal.role  || null,
        email:             editModal.email || null,
        receiveGroup:      editModal.receiveGroup,
        receiveIndividual: editModal.receiveIndividual,
        receiveMeetings:   editModal.receiveMeetings,
      })
      setRecipients((prev) => prev.map((r) => r.id === editModal.id ? res.data : r))
      setEditModal(null)
    } catch (err) {
      setEditErr(err.response?.data?.error || 'Failed to update recipient.')
    } finally {
      setEditSaving(false)
    }
  }

  function openEdit(r) {
    setEditModal({
      id:               r.id,
      name:             r.name,
      phoneNumber:      r.phoneNumber,
      role:             r.role  || '',
      email:            r.email || '',
      receiveGroup:     r.receiveGroup,
      receiveIndividual: r.receiveIndividual,
      receiveMeetings:  r.receiveMeetings,
    })
    setEditErr(null)
  }

  return (
    <div className="space-y-5">

      {/* Edit modal */}
      {editModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-md mx-4 p-6">
            <h3 className="text-sm font-semibold text-gray-800 mb-4">Edit Recipient</h3>
            {editErr && <p className="text-xs text-red-500 mb-3">{editErr}</p>}
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs text-gray-500 block mb-1">Name *</label>
                  <input value={editModal.name} onChange={setEditField('name')}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-300" autoFocus />
                </div>
                <div>
                  <label className="text-xs text-gray-500 block mb-1">Role</label>
                  <input value={editModal.role} onChange={setEditField('role')}
                    placeholder="Surgeon, Admin…"
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-300" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs text-gray-500 block mb-1">Phone (international)</label>
                  <input value={editModal.phoneNumber} onChange={setEditField('phoneNumber')}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-brand-300" />
                </div>
                <div>
                  <label className="text-xs text-gray-500 block mb-1">Email</label>
                  <input value={editModal.email} onChange={setEditField('email')}
                    type="email" placeholder="name@example.com"
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-300" />
                </div>
              </div>
              <div className="flex gap-5 pt-1 flex-wrap">
                <label className="flex items-center gap-2 text-xs text-gray-600 cursor-pointer">
                  <input type="checkbox" checked={editModal.receiveIndividual}
                    onChange={(e) => setEditModal((m) => ({ ...m, receiveIndividual: e.target.checked }))}
                    className="w-3.5 h-3.5 accent-brand-600" />
                  Individual uploads
                </label>
                <label className="flex items-center gap-2 text-xs text-gray-600 cursor-pointer">
                  <input type="checkbox" checked={editModal.receiveGroup}
                    onChange={(e) => setEditModal((m) => ({ ...m, receiveGroup: e.target.checked }))}
                    className="w-3.5 h-3.5 accent-brand-600" />
                  Group/session uploads
                </label>
                <label className="flex items-center gap-2 text-xs text-gray-600 cursor-pointer">
                  <input type="checkbox" checked={!!editModal.receiveMeetings}
                    onChange={(e) => setEditModal((m) => ({ ...m, receiveMeetings: e.target.checked }))}
                    className="w-3.5 h-3.5 accent-brand-600" />
                  Planning meeting invites
                </label>
              </div>
            </div>
            <div className="flex gap-2 justify-end mt-5">
              <button onClick={() => setEditModal(null)} disabled={editSaving}
                className="text-sm px-4 py-2 rounded-lg border border-gray-200 text-gray-600 hover:bg-gray-50 transition-colors">
                Cancel
              </button>
              <button onClick={handleEditSave} disabled={editSaving}
                className="text-sm px-4 py-2 rounded-lg bg-brand-600 text-white hover:bg-brand-700 transition-colors disabled:opacity-60 flex items-center gap-2">
                {editSaving && (
                  <svg className="w-3.5 h-3.5 animate-spin" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
                  </svg>
                )}
                {editSaving ? 'Saving…' : 'Save changes'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Confirm permanent delete modal */}
      {deleteConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-sm mx-4 p-6">
            <div className="flex items-start gap-3 mb-4">
              <div className="w-9 h-9 rounded-full bg-red-100 flex items-center justify-center flex-shrink-0">
                <svg className="w-5 h-5 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                </svg>
              </div>
              <div>
                <h3 className="text-sm font-semibold text-gray-800">Delete recipient?</h3>
                <p className="text-xs text-gray-500 mt-1">
                  <span className="font-medium text-gray-700">{deleteConfirm.name}</span> will be permanently removed along with all their notification history.
                </p>
              </div>
            </div>
            <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 mb-5">
              This action cannot be undone.
            </p>
            <div className="flex gap-2 justify-end">
              <button onClick={() => setDeleteConfirm(null)} disabled={deleting}
                className="text-sm px-4 py-2 rounded-lg border border-gray-200 text-gray-600 hover:bg-gray-50 transition-colors">
                Cancel
              </button>
              <button onClick={handleDeletePermanent} disabled={deleting}
                className="text-sm px-4 py-2 rounded-lg bg-red-600 text-white hover:bg-red-700 transition-colors disabled:opacity-60 flex items-center gap-2">
                {deleting && (
                  <svg className="w-3.5 h-3.5 animate-spin" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
                  </svg>
                )}
                {deleting ? 'Deleting…' : 'Delete permanently'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Add form (admin only) */}
      {isAdmin && (
        <div className="bg-white rounded-xl border border-gray-200 p-5">
          <h3 className="text-sm font-semibold text-gray-700 mb-4">Add Recipient</h3>
          {addErr && <p className="text-xs text-red-500 mb-3">{addErr}</p>}
          <div className="flex items-end gap-3 flex-wrap">
            <LabelledInput label="Name *"        value={form.name}        onChange={setField('name')}        placeholder="John Doe"          width="w-36" />
            <LabelledInput label="Phone * (intl)" value={form.phoneNumber} onChange={setField('phoneNumber')} placeholder="+447700900000"    width="w-44" />
            <LabelledInput label="Role"           value={form.role}        onChange={setField('role')}        placeholder="Surgeon, Admin…"   width="w-32" />
            <LabelledInput label="Email"          value={form.email}       onChange={setField('email')}       placeholder="name@example.com"  width="w-44" type="email" />
            <button onClick={handleAdd} disabled={adding} className="btn-primary text-sm">
              {adding ? 'Adding…' : 'Add Recipient'}
            </button>
          </div>
          <p className="text-xs text-gray-400 mt-2">
            Phone in international format without spaces, e.g. <span className="font-mono">447700900000</span>.
          </p>
        </div>
      )}

      {/* List */}
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        {loading ? (
          <div className="py-12 flex justify-center"><CentredSpinner /></div>
        ) : recipients.length === 0 ? (
          <p className="py-12 text-center text-gray-400 text-sm">No recipients configured yet.</p>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-gray-100 text-[11px] text-gray-400 uppercase tracking-wide bg-gray-50">
                <th className="px-5 py-3 text-left font-medium">Name / Email</th>
                <th className="px-3 py-3 text-left font-medium">Phone</th>
                <th className="px-3 py-3 text-left font-medium">Role</th>
                <th className="px-3 py-3 text-left font-medium">Notified on</th>
                <th className="px-3 py-3 text-left font-medium">Status</th>
                <th className="px-3 py-3 text-right font-medium">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {recipients.map((r) => (
                <tr key={r.id} className={`hover:bg-gray-50 transition-colors ${!r.isActive ? 'opacity-50' : ''}`}>
                  <td className="px-5 py-3">
                    <p className="font-medium text-gray-800">{r.name}</p>
                    {r.email && <p className="text-xs text-gray-400 mt-0.5">{r.email}</p>}
                  </td>
                  <td className="px-3 py-3 font-mono text-gray-600 text-xs">{r.phoneNumber}</td>
                  <td className="px-3 py-3 text-gray-500">{r.role || '—'}</td>
                  <td className="px-3 py-3 text-gray-400 text-xs">
                    {[
                      r.receiveIndividual && 'Individual',
                      r.receiveGroup && 'Group',
                      r.receiveMeetings && 'Meetings',
                    ].filter(Boolean).join(' · ') || '—'}
                  </td>
                  <td className="px-3 py-3">
                    <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${r.isActive ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-400'}`}>
                      {r.isActive ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td className="px-3 py-3">
                    <div className="flex items-center justify-end gap-2">
                      {r.isActive && (
                        <button
                          onClick={() => handleTest(r.id)}
                          disabled={!!testing}
                          className={[
                            'text-xs px-2.5 py-1 rounded-lg border transition-colors',
                            testResult[r.id] === 'ok'    ? 'border-green-300 bg-green-50 text-green-700'
                            : testResult[r.id] === 'error' ? 'border-red-300 bg-red-50 text-red-600'
                            : 'border-gray-200 text-gray-600 hover:bg-gray-50',
                          ].join(' ')}
                        >
                          {testing === r.id ? 'Sending…' : testResult[r.id] === 'ok' ? '✓ Sent' : testResult[r.id] === 'error' ? '✗ Failed' : 'Test'}
                        </button>
                      )}
                      {/* Edit (admin only) */}
                      {isAdmin && <button onClick={() => openEdit(r)} title="Edit recipient"
                        className="text-gray-300 hover:text-blue-500 hover:bg-blue-50 transition-colors p-1 rounded-lg">
                        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.75}
                            d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                        </svg>
                      </button>}
                      {/* Deactivate (admin only) */}
                      {isAdmin && r.isActive && (
                        <button onClick={() => handleRemove(r.id, r.name)} disabled={removing === r.id}
                          title="Deactivate recipient"
                          className="text-gray-300 hover:text-yellow-500 transition-colors p-1 disabled:opacity-40 rounded-lg">
                          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                          </svg>
                        </button>
                      )}
                      {/* Delete (admin only) */}
                      {isAdmin && <button onClick={() => setDeleteConfirm({ id: r.id, name: r.name })}
                        title="Permanently delete recipient"
                        className="text-gray-300 hover:text-red-500 transition-colors p-1 rounded-lg">
                        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                        </svg>
                      </button>}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

    </div>
  )
}

// ── Shared sub-components ─────────────────────────────────────────
function LabelledInput({ label, value, onChange, placeholder, width = 'w-40', type = 'text' }) {
  return (
    <div>
      <label className="text-xs text-gray-500 block mb-1">{label}</label>
      <input
        type={type}
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        className={`${width} border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-300`}
      />
    </div>
  )
}

// ═════════════════════════════════════════════════════════════════
// Orphans Tab
// ═════════════════════════════════════════════════════════════════
function OrphansTab({ role }) {
  const isAdmin = role === 'admin'
  const [orphans, setOrphans]   = useState([])
  const [total, setTotal]       = useState(0)
  const [loading, setLoading]   = useState(true)
  const [purging, setPurging]   = useState(false)

  const load = useCallback(async () => {
    try {
      const { data } = await getOrphanUploads()
      setOrphans(data.orphans)
      setTotal(data.total)
    } catch { /* ignore */ }
    finally { setLoading(false) }
  }, [])

  useEffect(() => { load() }, [load])

  async function handlePurgeAll() {
    if (!window.confirm(`Delete all ${total} orphan record(s)? This cannot be undone.`)) return
    setPurging(true)
    try {
      await purgeAllOrphans()
      setOrphans([])
      setTotal(0)
    } catch { /* ignore */ }
    finally { setPurging(false) }
  }

  async function handleDeleteOne(id) {
    try {
      await deleteOrphan(id)
      setOrphans(prev => prev.filter(o => o.id !== id))
      setTotal(prev => prev - 1)
    } catch { /* ignore */ }
  }

  if (loading) return <CentredSpinner />

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold text-gray-800">Orphan Uploads</h2>
          <p className="text-sm text-gray-500 mt-0.5">
            Upload records with no Drive file — initiated but never completed.
            Records older than 24 h are auto-purged hourly.
          </p>
        </div>
        {isAdmin && total > 0 && (
          <button
            onClick={handlePurgeAll}
            disabled={purging}
            className="btn-danger text-sm px-4 py-2"
          >
            {purging ? 'Purging…' : `Purge all (${total})`}
          </button>
        )}
      </div>

      {total === 0 ? (
        <div className="bg-white border border-gray-200 rounded-xl p-8 text-center text-gray-400">
          No orphan uploads found. All clean!
        </div>
      ) : (
        <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="text-left px-4 py-3 font-medium text-gray-600">Case ID</th>
                <th className="text-left px-4 py-3 font-medium text-gray-600">Filename</th>
                <th className="text-right px-4 py-3 font-medium text-gray-600">Size</th>
                <th className="text-right px-4 py-3 font-medium text-gray-600">Age</th>
                <th className="text-center px-4 py-3 font-medium text-gray-600">Auto-cleanup</th>
                <th className="text-right px-4 py-3 font-medium text-gray-600"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {orphans.map(o => (
                <tr key={o.id} className="hover:bg-gray-50">
                  <td className="px-4 py-3 font-mono text-xs text-gray-700">{o.caseId}</td>
                  <td className="px-4 py-3 text-gray-700 truncate max-w-[200px]">{o.filename}</td>
                  <td className="px-4 py-3 text-right text-gray-500">{formatBytes(o.fileSize)}</td>
                  <td className="px-4 py-3 text-right text-gray-500">{o.ageHours}h ago</td>
                  <td className="px-4 py-3 text-center">
                    {o.autoCleanup
                      ? <span className="inline-block px-2 py-0.5 text-xs rounded-full bg-yellow-100 text-yellow-700">Pending</span>
                      : <span className="inline-block px-2 py-0.5 text-xs rounded-full bg-gray-100 text-gray-400">&lt;24h</span>
                    }
                  </td>
                  <td className="px-4 py-3 text-right">
                    {isAdmin && <button
                      onClick={() => handleDeleteOne(o.id)}
                      className="text-red-500 hover:text-red-700 text-xs font-medium"
                    >
                      Delete
                    </button>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}

function Spinner({ className = 'w-5 h-5' }) {
  return (
    <svg className={`${className} animate-spin`} fill="none" viewBox="0 0 24 24">
      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z" />
    </svg>
  )
}


// ═════════════════════════════════════════════════════════════════
// Meet tab — Google Meet lifecycle management
// ═════════════════════════════════════════════════════════════════

const MEET_STATUS_BADGE = {
  SCHEDULED: 'bg-blue-100 text-blue-700',
  ACTIVE:    'bg-green-100 text-green-700',
  ENDED:     'bg-gray-100 text-gray-600',
  CANCELLED: 'bg-red-100 text-red-600',
}

function MeetTab({ role }) {
  const isAdmin = role === 'admin'
  const [view, setView]               = useState('upcoming')
  const [meetings, setMeetings]       = useState([])
  const [loading, setLoading]         = useState(true)
  const [total, setTotal]             = useState(0)
  const [page, setPage]               = useState(1)
  const [calConnected, setCalConnected] = useState(null) // null = loading, true/false
  const [calEmail, setCalEmail]       = useState(null)

  // Reschedule inline state
  const [rescheduleId, setRescheduleId]       = useState(null)
  const [rescheduleDate, setRescheduleDate]   = useState('')
  const [rescheduleTime, setRescheduleTime]   = useState('')
  const [rescheduleDur, setRescheduleDur]     = useState(60)
  const [rescheduling, setRescheduling]       = useState(false)

  // Cancel confirmation
  const [cancelId, setCancelId]       = useState(null)
  const [cancelReason, setCancelReason] = useState('')
  const [cancelling, setCancelling]   = useState(false)

  const [copied, setCopied]           = useState(null)
  const [deleting, setDeleting]       = useState(null) // meetingId or 'bulk'

  function load() {
    setLoading(true)
    getAllMeetings({ view, page, limit: 50 })
      .then(r => { setMeetings(r.data.meetings); setTotal(r.data.total) })
      .catch(() => {})
      .finally(() => setLoading(false))
  }

  useEffect(() => { load() }, [view, page])
  useEffect(() => {
    getCalendarStatus()
      .then(r => { setCalConnected(r.data.connected); setCalEmail(r.data.email) })
      .catch(() => setCalConnected(false))
  }, [])

  function handleReschedule(m) {
    const dt = new Date(m.scheduledAt)
    setRescheduleId(m.id)
    setRescheduleDate(dt.toISOString().slice(0, 10))
    setRescheduleTime(dt.toTimeString().slice(0, 5))
    setRescheduleDur(m.durationMin)
  }

  async function submitReschedule() {
    if (!rescheduleDate || !rescheduleTime) return
    setRescheduling(true)
    try {
      const scheduledAt = new Date(`${rescheduleDate}T${rescheduleTime}`).toISOString()
      await rescheduleMeeting(rescheduleId, { scheduledAt, durationMin: rescheduleDur })
      setRescheduleId(null)
      load()
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to reschedule')
    } finally {
      setRescheduling(false)
    }
  }

  async function submitCancel() {
    setCancelling(true)
    try {
      await cancelMeeting(cancelId, { reason: cancelReason || undefined })
      setCancelId(null)
      setCancelReason('')
      load()
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to cancel')
    } finally {
      setCancelling(false)
    }
  }

  function copyLink(url) {
    navigator.clipboard.writeText(url)
    setCopied(url)
    setTimeout(() => setCopied(null), 2000)
  }

  async function handleDeleteMeeting(id) {
    setDeleting(id)
    try {
      await deleteMeeting(id)
      load()
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to delete')
    } finally {
      setDeleting(null)
    }
  }

  async function handleBulkDelete() {
    if (!window.confirm(`Delete all ${view} meetings? This cannot be undone.`)) return
    setDeleting('bulk')
    try {
      const { data } = await bulkDeleteMeetings(view)
      load()
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to delete')
    } finally {
      setDeleting(null)
    }
  }

  // Group upcoming meetings by day
  function groupByDay(list) {
    const groups = {}
    const today = new Date().toDateString()
    const tomorrow = new Date(Date.now() + 86400000).toDateString()
    for (const m of list) {
      const d = new Date(m.scheduledAt).toDateString()
      const label = d === today ? 'Today' : d === tomorrow ? 'Tomorrow' : new Date(m.scheduledAt).toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' })
      if (!groups[label]) groups[label] = []
      groups[label].push(m)
    }
    return Object.entries(groups)
  }

  if (loading && meetings.length === 0) return <CentredSpinner />

  const grouped = view === 'upcoming' ? groupByDay(meetings) : null

  return (
    <div className="space-y-4">
      {/* Calendar connection banner */}
      {calConnected === false && (
        <div className="bg-amber-50 border border-amber-200 rounded-xl px-4 py-3 flex items-center justify-between">
          <div>
            <p className="text-sm font-medium text-amber-800">Calendar not connected</p>
            <p className="text-xs text-amber-600">Connect the planning calendar account to enable Google Meet invites.</p>
          </div>
          <a
            href={`${import.meta.env.VITE_API_BASE_URL || ''}/api/admin/auth/calendar?redirect=${encodeURIComponent(window.location.href)}`}
            className="text-xs px-3 py-1.5 rounded-lg bg-amber-600 text-white hover:bg-amber-700 transition-colors font-medium whitespace-nowrap"
          >
            Connect Calendar
          </a>
        </div>
      )}
      {calConnected === true && calEmail && (
        <div className="flex items-center gap-2 text-xs text-gray-400">
          <span className="w-2 h-2 bg-green-400 rounded-full" />
          Calendar connected: {calEmail}
        </div>
      )}

      {/* View toggle */}
      <div className="flex items-center gap-2">
        {['upcoming', 'past', 'cancelled'].map(v => (
          <button
            key={v}
            onClick={() => { setView(v); setPage(1) }}
            className={`text-sm px-4 py-2 rounded-lg border transition-colors font-medium capitalize ${
              view === v ? 'bg-brand-600 text-white border-brand-600' : 'border-gray-300 text-gray-700 hover:bg-gray-50'
            }`}
          >
            {v}
          </button>
        ))}
        <span className="text-sm text-gray-400 ml-auto">{total} meeting{total !== 1 ? 's' : ''}</span>
        {isAdmin && (view === 'past' || view === 'cancelled') && total > 0 && (
          <button
            onClick={handleBulkDelete}
            disabled={deleting === 'bulk'}
            className="text-xs px-3 py-1.5 rounded-lg border border-red-200 text-red-500 hover:bg-red-50 transition-colors font-medium disabled:opacity-50"
          >
            {deleting === 'bulk' ? 'Deleting…' : `Clear all ${view}`}
          </button>
        )}
      </div>

      {/* Meeting list */}
      {meetings.length === 0 ? (
        <div className="text-center py-12 text-gray-400 text-sm">
          {view === 'upcoming' ? 'No upcoming meetings' : view === 'past' ? 'No past meetings' : 'No cancelled meetings'}
        </div>
      ) : view === 'upcoming' && grouped ? (
        // Grouped by day
        grouped.map(([dayLabel, dayMeetings]) => (
          <div key={dayLabel}>
            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">{dayLabel}</p>
            <div className="space-y-2">
              {dayMeetings.map(m => renderMeetingCard(m))}
            </div>
          </div>
        ))
      ) : (
        <div className="space-y-2">
          {meetings.map(m => renderMeetingCard(m))}
        </div>
      )}

      {/* Pagination */}
      {total > 50 && (
        <div className="flex justify-center gap-2 pt-4">
          <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1} className="btn-secondary text-sm px-3 py-1.5 disabled:opacity-50">&larr;</button>
          <span className="text-sm text-gray-500 py-1.5">Page {page}</span>
          <button onClick={() => setPage(p => p + 1)} disabled={page * 50 >= total} className="btn-secondary text-sm px-3 py-1.5 disabled:opacity-50">&rarr;</button>
        </div>
      )}

      {/* Cancel confirmation modal */}
      {cancelId && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-sm mx-4 p-6">
            <h3 className="text-sm font-semibold text-gray-800 mb-3">Cancel Meeting</h3>
            <p className="text-xs text-gray-500 mb-3">All attendees will be notified of the cancellation.</p>
            <input
              type="text"
              value={cancelReason}
              onChange={e => setCancelReason(e.target.value)}
              placeholder="Reason (optional)"
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm mb-4 focus:outline-none focus:ring-2 focus:ring-brand-300"
            />
            <div className="flex gap-2 justify-end">
              <button onClick={() => { setCancelId(null); setCancelReason('') }} disabled={cancelling} className="text-sm px-4 py-2 rounded-lg border border-gray-200 text-gray-600 hover:bg-gray-50">
                Keep
              </button>
              <button onClick={submitCancel} disabled={cancelling} className="text-sm px-4 py-2 rounded-lg bg-red-600 text-white hover:bg-red-700 disabled:opacity-60">
                {cancelling ? 'Cancelling…' : 'Cancel Meeting'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )

  function renderMeetingCard(m) {
    const dt = new Date(m.scheduledAt)
    const timeStr = dt.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' })
    const dateStr = dt.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })
    const isScheduled = m.status === 'SCHEDULED'
    const isRescheduling = rescheduleId === m.id

    return (
      <div key={m.id} className="bg-white rounded-xl border border-gray-200 px-4 py-3">
        <div className="flex items-center gap-3">
          {/* Status badge */}
          <span className={`text-[11px] px-2 py-0.5 rounded-full font-medium ${MEET_STATUS_BADGE[m.status] || 'bg-gray-100 text-gray-600'}`}>
            {m.status}
          </span>
          {/* Case ID */}
          <span className="text-sm font-semibold text-gray-800">{m.caseId}</span>
          {/* Time */}
          <span className="text-sm text-gray-600">{timeStr}</span>
          <span className="text-xs text-gray-400">{dateStr}</span>
          <span className="text-xs text-gray-400">{m.durationMin} min</span>
          {m.attendeeCount > 0 && (
            <span className="text-xs text-gray-400">{m.attendeeCount} attendee{m.attendeeCount !== 1 ? 's' : ''}</span>
          )}
          {m.notes && <span className="text-xs text-gray-400 truncate max-w-[150px]" title={m.notes}>{m.notes}</span>}

          {/* Actions */}
          <div className="flex items-center gap-1.5 ml-auto">
            {m.meetUrl && (
              <a href={m.meetUrl} target="_blank" rel="noopener noreferrer"
                className="text-xs px-2 py-1 rounded-lg border border-green-200 bg-green-50 text-green-700 hover:bg-green-100 transition-colors font-medium">
                Join
              </a>
            )}
            {isScheduled && (
              <>
                <button onClick={() => handleReschedule(m)}
                  className="text-xs px-2 py-1 rounded-lg border border-blue-200 text-blue-600 hover:bg-blue-50 transition-colors font-medium">
                  Reschedule
                </button>
                <button onClick={() => setCancelId(m.id)}
                  className="text-xs px-2 py-1 rounded-lg border border-red-200 text-red-500 hover:bg-red-50 transition-colors font-medium">
                  Cancel
                </button>
              </>
            )}
            {m.meetUrl && (
              <button onClick={() => copyLink(m.meetUrl)}
                className="text-xs px-2 py-1 rounded-lg border border-gray-200 text-gray-500 hover:bg-gray-50 transition-colors">
                {copied === m.meetUrl ? '✓ Copied' : 'Copy'}
              </button>
            )}
            {isAdmin && !isScheduled && (
              <button onClick={() => handleDeleteMeeting(m.id)}
                disabled={deleting === m.id}
                className="text-xs px-2 py-1 rounded-lg border border-gray-200 text-gray-400 hover:text-red-500 hover:border-red-200 hover:bg-red-50 transition-colors">
                {deleting === m.id ? '…' : 'Delete'}
              </button>
            )}
          </div>
        </div>

        {/* Inline reschedule form */}
        {isRescheduling && (
          <div className="mt-3 pt-3 border-t border-gray-100 flex items-end gap-3">
            <div>
              <label className="text-xs text-gray-500 block mb-1">Date</label>
              <input type="date" value={rescheduleDate} onChange={e => setRescheduleDate(e.target.value)}
                className="border border-gray-300 rounded-lg px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-brand-300" />
            </div>
            <div>
              <label className="text-xs text-gray-500 block mb-1">Time</label>
              <input type="time" value={rescheduleTime} onChange={e => setRescheduleTime(e.target.value)}
                className="border border-gray-300 rounded-lg px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-brand-300" />
            </div>
            <div>
              <label className="text-xs text-gray-500 block mb-1">Duration</label>
              <select value={rescheduleDur} onChange={e => setRescheduleDur(Number(e.target.value))}
                className="border border-gray-300 rounded-lg px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-brand-300">
                <option value={30}>30 min</option><option value={60}>60 min</option>
                <option value={90}>90 min</option><option value={120}>2h</option>
              </select>
            </div>
            <button onClick={submitReschedule} disabled={rescheduling}
              className="text-xs px-3 py-1.5 rounded-lg bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-60 font-medium">
              {rescheduling ? 'Saving…' : 'Save'}
            </button>
            <button onClick={() => setRescheduleId(null)}
              className="text-xs px-3 py-1.5 rounded-lg border border-gray-200 text-gray-600 hover:bg-gray-50">
              Cancel
            </button>
          </div>
        )}

        {/* Cancelled info */}
        {m.status === 'CANCELLED' && m.cancelledAt && (
          <p className="text-xs text-gray-400 mt-2">Cancelled {new Date(m.cancelledAt).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}</p>
        )}
      </div>
    )
  }
}

function CentredSpinner() {
  return (
    <div className="flex justify-center py-8">
      <Spinner className="w-6 h-6 text-brand-500" />
    </div>
  )
}

// ══════════════════════════════════════════════════════════════════
// ══ Users Tab ════════════════════════════════════════════════════
// ══════════════════════════════════════════════════════════════════

function UsersTab() {
  const [users, setUsers] = useState([])
  const [loading, setLoading] = useState(true)
  const [newEmail, setNewEmail] = useState('')
  const [newName, setNewName] = useState('')
  const [newRole, setNewRole] = useState('designer')
  const [adding, setAdding] = useState(false)
  const [error, setError] = useState(null)

  const fetchUsers = useCallback(async () => {
    try {
      const { data } = await getAdminUsers()
      setUsers(data.users)
    } catch (err) {
      setError('Failed to load users')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => { fetchUsers() }, [fetchUsers])

  const handleAdd = async (e) => {
    e.preventDefault()
    if (!newEmail.trim()) return
    setAdding(true)
    setError(null)
    try {
      await addAdminUser({ email: newEmail.trim(), name: newName.trim() || undefined, role: newRole })
      setNewEmail('')
      setNewName('')
      setNewRole('designer')
      fetchUsers()
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to add user')
    } finally {
      setAdding(false)
    }
  }

  const handleRemove = async (user) => {
    if (!window.confirm(`Remove ${user.email} from authorised users?`)) return
    try {
      await removeAdminUser(user.id)
      fetchUsers()
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to remove user')
    }
  }

  if (loading) return <CentredSpinner />

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold text-gray-800">Authorised Users</h2>
          <p className="text-sm text-gray-500 mt-0.5">
            Users listed here can access the Admin Portal. Add or remove team members as needed.
          </p>
        </div>
        <span className="text-sm text-gray-400">{users.length} user{users.length !== 1 ? 's' : ''}</span>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 text-sm px-4 py-3 rounded-lg flex justify-between items-center">
          {error}
          <button onClick={() => setError(null)} className="text-red-400 hover:text-red-600 ml-4">&times;</button>
        </div>
      )}

      {/* Add user form */}
      <form onSubmit={handleAdd} className="card p-4">
        <p className="text-sm font-medium text-gray-700 mb-3">Add New User</p>
        <div className="flex gap-3">
          <input
            type="email"
            value={newEmail}
            onChange={e => setNewEmail(e.target.value)}
            placeholder="Email address (Google account)"
            required
            className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
          />
          <input
            type="text"
            value={newName}
            onChange={e => setNewName(e.target.value)}
            placeholder="Name (optional)"
            className="w-48 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
          />
          <select
            value={newRole}
            onChange={e => setNewRole(e.target.value)}
            className="w-36 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
          >
            <option value="designer">Designer</option>
            <option value="admin">Admin</option>
          </select>
          <button
            type="submit"
            disabled={adding || !newEmail.trim()}
            className="btn-primary px-5 py-2 text-sm whitespace-nowrap disabled:opacity-50"
          >
            {adding ? 'Adding...' : 'Add User'}
          </button>
        </div>
        <p className="text-xs text-gray-400 mt-2">
          The user must have a Google account with this email. They will be able to sign in and access the Admin Portal.
        </p>
      </form>

      {/* Users list */}
      <div className="card overflow-hidden">
        <table className="w-full">
          <thead>
            <tr className="bg-gray-50 border-b border-gray-200">
              <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">Email</th>
              <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">Name</th>
              <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">Role</th>
              <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">Added By</th>
              <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">Added</th>
              <th className="px-4 py-3"></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {users.map(u => (
              <tr key={u.id} className="hover:bg-gray-50">
                <td className="px-4 py-3 text-sm text-gray-800 font-medium">{u.email}</td>
                <td className="px-4 py-3 text-sm text-gray-600">{u.name || '—'}</td>
                <td className="px-4 py-3">
                  <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${
                    u.role === 'admin' ? 'bg-purple-100 text-purple-700' : 'bg-blue-100 text-blue-700'
                  }`}>
                    {u.role || 'admin'}
                  </span>
                </td>
                <td className="px-4 py-3 text-sm text-gray-500">{u.addedBy || 'system'}</td>
                <td className="px-4 py-3 text-sm text-gray-500">
                  {new Date(u.createdAt).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })}
                </td>
                <td className="px-4 py-3 text-right">
                  <button
                    onClick={() => handleRemove(u)}
                    className="text-red-500 hover:text-red-700 text-sm font-medium"
                  >
                    Remove
                  </button>
                </td>
              </tr>
            ))}
            {users.length === 0 && (
              <tr>
                <td colSpan={6} className="px-4 py-8 text-center text-sm text-gray-400">
                  No users added yet. Users in the ADMIN_ALLOWED_EMAILS environment variable can still sign in.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <p className="text-xs text-gray-400">
        Note: Users listed in the ADMIN_ALLOWED_EMAILS environment variable on Cloud Run also have access, regardless of this list.
      </p>
    </div>
  )
}

// ═════════════════════════════════════════════════════════════════
// Licenses tab — unified license management for all products
// ═════════════════════════════════════════════════════════════════

const STATUS_PILL = {
  ACTIVE:    'bg-green-100 text-green-700',
  EXPIRED:   'bg-amber-100 text-amber-700',
  REVOKED:   'bg-red-100 text-red-600',
  SUSPENDED: 'bg-gray-100 text-gray-600',
}

// SSE update state. Deliberately the same three colours the rollout console used, so
// the merge does not silently change what a colour means to whoever reads this table.
const UPDATE_PILL = {
  'up-to-date': 'bg-green-100 text-green-700',
  pending:      'bg-amber-100 text-amber-700',
  'no-target':  'bg-gray-100 text-gray-500',
}

function LicensesTab({ role }) {
  const isAdmin = role === 'admin'
  const [licenses,    setLicenses]    = useState([])
  const [products,    setProducts]    = useState([])
  const [total,       setTotal]       = useState(0)
  const [page,        setPage]        = useState(1)
  const [search,      setSearch]      = useState('')
  const [filterProd,  setFilterProd]  = useState('')
  const [filterStat,  setFilterStat]  = useState('')
  const [loading,     setLoading]     = useState(true)
  const [showCreate,  setShowCreate]  = useState(false)
  const [creating,    setCreating]    = useState(false)
  const [createErr,   setCreateErr]   = useState(null)
  const [expandedId,  setExpandedId]  = useState(null)
  const [detail,      setDetail]      = useState(null)
  const [detailLoad,  setDetailLoad]  = useState(false)
  const [revoking,    setRevoking]    = useState(null)
  // Expiry on an ALREADY-ISSUED licence. The Issue form only offers an end date for SUBSCRIPTION,
  // but the backend enforces expiry on whatever carries the field, type regardless — so a
  // device-locked PERMANENT licence can be made to run out on its own. That is what an external
  // evaluation needs, and there was no way to set it after issue.
  const [expiryDraft, setExpiryDraft] = useState('')
  const [expirySaving, setExpirySaving] = useState(false)
  const [expiryErr,   setExpiryErr]   = useState(null)
  const [revokeModal, setRevokeModal] = useState(null)
  const [revokeReason, setRevokeReason] = useState('')
  const [deleteModal, setDeleteModal] = useState(null)   // { id, label } | null
  const [deleting,    setDeleting]    = useState(false)
  const [deleteErr,   setDeleteErr]   = useState(null)
  const [purgeModal,  setPurgeModal]  = useState(false)
  const [purging,     setPurging]     = useState(false)
  const [purgeErr,    setPurgeErr]    = useState(null)
  const [copied,      setCopied]      = useState(null)
  const [stats,       setStats]       = useState(null)
  const searchTimer = useRef(null)
  const LIMIT = 20

  // SSE per-machine update target. The versions list comes from the registered
  // releases; `targetSaving` disables just the row being written.
  const [updateVersions, setUpdateVersions] = useState([])
  const [targetSaving,   setTargetSaving]   = useState(null)
  const [targetErr,      setTargetErr]      = useState(null)

  // New product form state. A product row must exist before any license can be issued
  // against it, and creating one was API-only until now — which is why a finished app
  // could sit unusable waiting on a row nobody had a way to add.
  const [showNewProd, setShowNewProd] = useState(false)
  const [prodSaving,  setProdSaving]  = useState(false)
  const [prodErr,     setProdErr]     = useState(null)
  const [npSlug,      setNpSlug]      = useState('')
  const [npName,      setNpName]      = useState('')
  const [npBrand,     setNpBrand]     = useState('osteo3d')

  // New license form state
  const [newProduct,  setNewProduct]  = useState('')
  const [newType,     setNewType]     = useState('PERMANENT')
  const [newDevice,   setNewDevice]   = useState('')
  const [newEmail,    setNewEmail]    = useState('')
  const [newName,     setNewName]     = useState('')
  const [newExpiry,   setNewExpiry]   = useState('')
  const [newGrace,    setNewGrace]    = useState('')
  const [newNotes,    setNewNotes]    = useState('')

  const load = useCallback((p, q) => {
    setLoading(true)
    const params = { page: p, limit: LIMIT }
    if (q) params.search = q
    if (filterProd) params.product = filterProd
    if (filterStat) params.status = filterStat
    getLicenses(params)
      .then((res) => {
        setLicenses(res.data.licenses)
        setTotal(res.data.total)
      })
      .catch(() => {})
      .finally(() => setLoading(false))
  }, [filterProd, filterStat])

  const loadProducts = useCallback(
    () => getLicenseProducts().then(r => setProducts(r.data)).catch(() => {}),
    [],
  )

  useEffect(() => { load(1, '') }, [load])
  useEffect(() => {
    loadProducts()
    getLicenseStats().then(r => setStats(r.data)).catch(() => {})
    // Registered SSE releases populate the per-machine target dropdown. A failure
    // here must not affect the licence list — the column simply offers no versions.
    if (isAdmin) loadVersions()
  }, [isAdmin, loadProducts])

  // Newest first, compared NUMERICALLY: a lexicographic sort puts 0.16.9 above
  // 0.16.16, which is already wrong today.
  const loadVersions = useCallback(() => {
    if (!isAdmin) return Promise.resolve()
    return getSseReleases()
      .then(r => {
        const cmp = (a, b) => {
          const pa = String(a).split('.').map(Number), pb = String(b).split('.').map(Number)
          for (let i = 0; i < Math.max(pa.length, pb.length); i++) {
            const d = (pb[i] || 0) - (pa[i] || 0)
            if (d) return d
          }
          return 0
        }
        setUpdateVersions([...new Set((r.data.releases || []).map(x => x.version))].sort(cmp))
      })
      .catch(() => {})
  }, [isAdmin])

  // Pin one machine to a version, or clear it back to the fleet default. Same
  // endpoint the rollout console used; the licence row is just a better home for it.
  async function saveTarget(deviceCode, targetVersion) {
    setTargetSaving(deviceCode); setTargetErr(null)
    try {
      await setSseWorkstation(deviceCode, { targetVersion })
      load(page, search)
    } catch (e) {
      // Without this the only symptom is the select silently snapping back, which
      // makes any real failure — permissions, network, a missing row — undiagnosable.
      setTargetErr(e.response?.data?.error || `Could not set the target for ${deviceCode}.`)
    } finally { setTargetSaving(null) }
  }

  function onSearch(e) {
    const q = e.target.value
    setSearch(q)
    setPage(1)
    clearTimeout(searchTimer.current)
    searchTimer.current = setTimeout(() => load(1, q), 300)
  }

  async function handleCreate(e) {
    e.preventDefault()
    setCreateErr(null)
    setCreating(true)
    try {
      const data = {
        productSlug: newProduct,
        type: newType,
        ...(newType === 'PERMANENT' ? { deviceCode: newDevice } : { email: newEmail }),
        ...(newName && { customerName: newName }),
        ...(newExpiry && { expiresAt: newExpiry }),
        ...(newGrace && { offlineGraceDays: parseInt(newGrace) }),
        ...(newNotes && { notes: newNotes }),
      }
      const res = await issueLicense(data)
      // Show token in a copy-able prompt
      const token = res.data.token
      setCopied(token)
      setShowCreate(false)
      setNewProduct(''); setNewType('PERMANENT'); setNewDevice(''); setNewEmail('')
      setNewName(''); setNewExpiry(''); setNewGrace(''); setNewNotes('')
      load(page, search)
      // Refresh stats
      getLicenseStats().then(r => setStats(r.data)).catch(() => {})
    } catch (err) {
      setCreateErr(err.response?.data?.error || 'Failed to create license')
    } finally {
      setCreating(false)
    }
  }

  // The slug is what a shipped binary sends in its activation call. It is compiled in,
  // so a typo here cannot be corrected from the server side afterwards — the installed
  // copies would keep sending the old one. Hence the strict shape check before POSTing.
  async function handleCreateProduct(e) {
    e.preventDefault()
    const slug = normaliseProductSlug(npSlug)
    if (!isValidProductSlug(slug)) {
      setProdErr(PRODUCT_SLUG_RULE)
      return
    }
    setProdErr(null)
    setProdSaving(true)
    try {
      await createLicenseProduct({ slug, name: npName.trim(), brand: npBrand.trim() || 'osteo3d' })
      await loadProducts()
      setShowNewProd(false)
      setNpSlug(''); setNpName(''); setNpBrand('osteo3d')
      // Preselect it, since creating a product is nearly always followed by issuing
      // the first licence against it.
      setNewProduct(slug)
      setShowCreate(true)
    } catch (err) {
      setProdErr(err.response?.data?.error || 'Failed to create product')
    } finally {
      setProdSaving(false)
    }
  }

  async function loadDetail(id) {
    if (expandedId === id) { setExpandedId(null); return }
    setExpandedId(id)
    setDetailLoad(true)
    setExpiryErr(null)
    try {
      const res = await getLicenseDetail(id)
      setDetail(res.data)
      // <input type="date"> wants YYYY-MM-DD. Slicing the ISO string keeps the stored UTC day
      // rather than shifting it through the browser's timezone, which in IST would show the
      // previous day for anything stored at a UTC midnight.
      setExpiryDraft(res.data.expiresAt ? new Date(res.data.expiresAt).toISOString().slice(0, 10) : '')
    } catch { setDetail(null); setExpiryDraft('') }
    finally { setDetailLoad(false) }
  }

  // Set or clear an end date on an issued licence. `null` clears it; the route turns that into a
  // real NULL rather than the epoch.
  async function saveExpiry(id, value) {
    setExpirySaving(true)
    setExpiryErr(null)
    try {
      // End of the chosen day, so a licence dated today still works for the rest of today.
      await updateLicense(id, { expiresAt: value ? new Date(`${value}T23:59:59`).toISOString() : null })
      const res = await getLicenseDetail(id)
      setDetail(res.data)
      setExpiryDraft(res.data.expiresAt ? new Date(res.data.expiresAt).toISOString().slice(0, 10) : '')
      load(page, search)
      getLicenseStats().then(r => setStats(r.data)).catch(() => {})
    } catch (err) {
      setExpiryErr(err?.response?.data?.error || 'Could not save the expiry date')
    }
    finally { setExpirySaving(false) }
  }

  async function handleRevoke() {
    if (!revokeModal) return
    setRevoking(revokeModal)
    try {
      await revokeLicense(revokeModal, revokeReason)
      setRevokeModal(null)
      setRevokeReason('')
      load(page, search)
      getLicenseStats().then(r => setStats(r.data)).catch(() => {})
      if (expandedId === revokeModal) { setExpandedId(null); setDetail(null) }
    } catch {}
    finally { setRevoking(null) }
  }

  async function handleDelete() {
    if (!deleteModal) return
    const { id, label } = deleteModal  // capture before setState
    setDeleting(true)
    setDeleteErr(null)
    try {
      await deleteLicense(id)
      setDeleteModal(null)
      if (expandedId === id) { setExpandedId(null); setDetail(null) }
      load(page, search)
      getLicenseStats().then(r => setStats(r.data)).catch(() => {})
    } catch (err) {
      setDeleteErr(err.response?.data?.error || 'Delete failed — try again')
    } finally { setDeleting(false) }
  }

  async function handleBulkPurge() {
    setPurging(true)
    setPurgeErr(null)
    try {
      const params = { status: filterStat || 'REVOKED' }
      if (filterProd) params.product = filterProd
      const res = await bulkPurgeLicenses(params)
      setPurgeModal(false)
      load(1, search)
      getLicenseStats().then(r => setStats(r.data)).catch(() => {})
      alert(`Deleted ${res.data.deleted} license(s).`)
    } catch (err) {
      setPurgeErr(err.response?.data?.error || 'Purge failed — try again')
    } finally { setPurging(false) }
  }

  function copyToken(token) {
    navigator.clipboard.writeText(token)
    setCopied('copied')
    setTimeout(() => setCopied(null), 2000)
  }

  const totalPages = Math.ceil(total / LIMIT)

  return (
    <div className="space-y-6">

      {/* Stats bar */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="card p-4">
            <p className="text-xs text-gray-400 uppercase tracking-wide">Total Licenses</p>
            <p className="text-2xl font-bold text-gray-800 mt-1">{stats.totalLicenses}</p>
          </div>
          <div className="card p-4">
            <p className="text-xs text-gray-400 uppercase tracking-wide">Active</p>
            <p className="text-2xl font-bold text-green-600 mt-1">{stats.activeLicenses}</p>
          </div>
          {stats.byProduct?.map(p => (
            <div key={p.slug} className="card p-4">
              <p className="text-xs text-gray-400 uppercase tracking-wide">{p.product}</p>
              <p className="text-2xl font-bold text-gray-800 mt-1">{p.count}</p>
            </div>
          ))}
        </div>
      )}

      {/* Token display (after creating a license) */}
      {copied && copied !== 'copied' && (
        <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
          <div className="flex items-center justify-between mb-2">
            <p className="text-sm font-semibold text-blue-800">License Token Created</p>
            <button onClick={() => setCopied(null)} className="text-blue-400 hover:text-blue-600">&times;</button>
          </div>
          <p className="text-xs text-blue-600 mb-2">Copy this token and send it to the user. It will not be shown again.</p>
          <div className="flex gap-2">
            <input readOnly value={copied} className="flex-1 font-mono text-xs px-3 py-2 border border-blue-200 rounded-lg bg-white" />
            <button onClick={() => copyToken(copied)} className="btn-primary text-xs px-3 py-2">
              Copy
            </button>
          </div>
        </div>
      )}

      {/* Toolbar */}
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div className="flex items-center gap-3 flex-1 min-w-0">
          <input
            type="text"
            value={search}
            onChange={onSearch}
            placeholder="Search by device code, email, or name…"
            className="input max-w-xs text-sm"
          />
          <select value={filterProd} onChange={e => { setFilterProd(e.target.value); setPage(1) }}
            className="input w-40 text-sm">
            <option value="">All products</option>
            {products.map(p => <option key={p.slug} value={p.slug}>{p.name}</option>)}
          </select>
          <select value={filterStat} onChange={e => { setFilterStat(e.target.value); setPage(1) }}
            className="input w-32 text-sm">
            <option value="">All status</option>
            <option value="ACTIVE">Active</option>
            <option value="EXPIRED">Expired</option>
            <option value="REVOKED">Revoked</option>
            <option value="SUSPENDED">Suspended</option>
          </select>
        </div>
        {isAdmin && filterStat && ['REVOKED','EXPIRED','SUSPENDED'].includes(filterStat) && (
          <button onClick={() => setPurgeModal(true)}
            className="btn-danger text-sm px-4 py-2">
            Purge {filterStat.toLowerCase()} {filterProd ? `(${filterProd})` : '(all products)'}
          </button>
        )}
        {isAdmin && <button onClick={() => { setShowNewProd(!showNewProd); setProdErr(null) }}
          className="btn-secondary text-sm px-4 py-2">
          {showNewProd ? 'Cancel' : 'New Product'}
        </button>}
        {isAdmin && <button onClick={() => setShowCreate(!showCreate)} className="btn-primary text-sm px-4 py-2">
          {showCreate ? 'Cancel' : 'Issue License'}
        </button>}
      </div>

      {/* Create product form (admin only). Products are the parent of licenses: a build
          that sends an unregistered slug cannot activate at all. */}
      {isAdmin && showNewProd && (
        <div className="card p-5">
          <h3 className="text-sm font-semibold text-gray-700 mb-3">New Licensable Product</h3>
          {prodErr && <p className="text-xs text-red-500 mb-2">{prodErr}</p>}
          <form onSubmit={handleCreateProduct} className="space-y-3">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <div>
                <label className="block text-xs text-gray-500 mb-1">Slug</label>
                <input value={npSlug} onChange={e => setNpSlug(e.target.value)} required
                  placeholder="spe" className="input text-sm w-full font-mono" />
                <p className="text-[11px] text-gray-400 mt-1">
                  Must match the slug the app sends when it activates.
                </p>
              </div>
              <div>
                <label className="block text-xs text-gray-500 mb-1">Name</label>
                <input value={npName} onChange={e => setNpName(e.target.value)} required
                  placeholder="Surgical Planning Engine" className="input text-sm w-full" />
              </div>
              <div>
                <label className="block text-xs text-gray-500 mb-1">Brand</label>
                <input value={npBrand} onChange={e => setNpBrand(e.target.value)}
                  placeholder="osteo3d" className="input text-sm w-full" />
              </div>
            </div>
            <div className="flex justify-end">
              <button type="submit" disabled={prodSaving} className="btn-primary text-sm px-4 py-2">
                {prodSaving ? 'Creating…' : 'Create Product'}
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Create license form (admin only) */}
      {isAdmin && showCreate && (
        <div className="card p-5">
          <h3 className="text-sm font-semibold text-gray-700 mb-3">Issue New License</h3>
          {createErr && <p className="text-xs text-red-500 mb-2">{createErr}</p>}
          <form onSubmit={handleCreate} className="space-y-3">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <div>
                <label className="block text-xs text-gray-500 mb-1">Product</label>
                <select value={newProduct} onChange={e => setNewProduct(e.target.value)} required
                  className="input text-sm w-full">
                  <option value="">Select product…</option>
                  {products.filter(p => p.isActive).map(p => (
                    <option key={p.slug} value={p.slug}>{p.name} ({p.brand})</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-xs text-gray-500 mb-1">Type</label>
                <select value={newType} onChange={e => setNewType(e.target.value)}
                  className="input text-sm w-full">
                  <option value="PERMANENT">Permanent (device-locked)</option>
                  <option value="SUBSCRIPTION">Subscription (time-limited)</option>
                </select>
              </div>
              {newType === 'PERMANENT' ? (
                <div>
                  <label className="block text-xs text-gray-500 mb-1">Device Code</label>
                  {/* 🔴 .trim() — a device code is nearly always PASTED, often out of a chat
                      window that carries a leading space with it. Uppercasing without trimming
                      issued a licence bound to " DEV-3C67-12E2-1ACB", which can never match the
                      machine and is invisible in the token. The backend normalises too; this is
                      what stops the wrong value being shown back as if it were accepted. */}
                  <input value={newDevice} onChange={e => setNewDevice(e.target.value.trim().toUpperCase())} required
                    placeholder="DEV-XXXX-XXXX-XXXX" className="input text-sm w-full font-mono" />
                </div>
              ) : (
                <div>
                  <label className="block text-xs text-gray-500 mb-1">Email</label>
                  <input type="email" value={newEmail} onChange={e => setNewEmail(e.target.value)} required
                    placeholder="customer@example.com" className="input text-sm w-full" />
                </div>
              )}
            </div>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
              <div>
                <label className="block text-xs text-gray-500 mb-1">Customer Name</label>
                <input value={newName} onChange={e => setNewName(e.target.value)}
                  placeholder="Optional" className="input text-sm w-full" />
              </div>
              {newType === 'SUBSCRIPTION' && (
                <div>
                  <label className="block text-xs text-gray-500 mb-1">Expires At</label>
                  <input type="datetime-local" value={newExpiry} onChange={e => setNewExpiry(e.target.value)}
                    className="input text-sm w-full" />
                </div>
              )}
              <div>
                <label className="block text-xs text-gray-500 mb-1">Offline Grace (days)</label>
                <input type="number" value={newGrace} onChange={e => setNewGrace(e.target.value)}
                  placeholder={newType === 'PERMANENT' ? '30' : '7'} className="input text-sm w-full" />
              </div>
              <div>
                <label className="block text-xs text-gray-500 mb-1">Notes</label>
                <input value={newNotes} onChange={e => setNewNotes(e.target.value)}
                  placeholder="Optional" className="input text-sm w-full" />
              </div>
            </div>
            <button type="submit" disabled={creating || !newProduct} className="btn-primary text-sm px-5 py-2">
              {creating ? 'Issuing…' : 'Issue License'}
            </button>
          </form>
        </div>
      )}

      {/* SSE release registry + fleet default. Per-machine state lives on the
          licence rows below — it is the same machine, not a second registry. */}
      {isAdmin && <SseUpdatesPanel onChange={() => { load(page, search); loadVersions() }} />}
      {targetErr && (
        <div className="mb-3 p-3 rounded-lg bg-red-50 text-red-700 text-sm flex items-start gap-2">
          <span className="flex-1">{targetErr}</span>
          <button onClick={() => setTargetErr(null)} className="text-red-500 hover:text-red-700 text-xs">Dismiss</button>
        </div>
      )}

      {/* License table */}
      <div className="bg-white rounded-xl border border-gray-200 overflow-x-auto">
        <table className="w-full text-sm min-w-[1000px]">
          <thead>
            <tr className="border-b border-gray-100 text-[11px] text-gray-400 uppercase tracking-wide bg-gray-50">
              <th className="px-4 py-3 text-left font-medium">Product</th>
              <th className="px-3 py-3 text-left font-medium">Type</th>
              <th className="px-3 py-3 text-left font-medium">Device / Email</th>
              <th className="px-3 py-3 text-left font-medium">Customer</th>
              {/* SSE update state — the workstation census is written by licence
                  validation, so it belongs on the machine's own row. */}
              <th className="px-3 py-3 text-left font-medium">Version</th>
              <th className="px-3 py-3 text-left font-medium">Update</th>
              <th className="px-3 py-3 text-center font-medium">Status</th>
              <th className="px-3 py-3 text-left font-medium">Issued</th>
              <th className="px-3 py-3 text-left font-medium">Expires</th>
              <th className="px-3 py-3 text-left font-medium">Last Check-in</th>
              <th className="px-3 py-3 text-right font-medium">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {loading && (
              <tr><td colSpan={11} className="px-4 py-8 text-center text-sm text-gray-400">Loading…</td></tr>
            )}
            {!loading && licenses.length === 0 && (
              <tr><td colSpan={11} className="px-4 py-8 text-center text-sm text-gray-400">No licenses found.</td></tr>
            )}
            {!loading && licenses.map(lic => (
              <Fragment key={lic.id}>
                <tr className="hover:bg-gray-50 cursor-pointer" onClick={() => loadDetail(lic.id)}>
                  <td className="px-4 py-3">
                    <span className="font-medium text-gray-800">{lic.product?.name}</span>
                    <span className="ml-1.5 text-[10px] text-gray-400">{lic.product?.brand}</span>
                  </td>
                  <td className="px-3 py-3 text-gray-600">{lic.type === 'PERMANENT' ? 'Permanent' : 'Subscription'}</td>
                  <td className="px-3 py-3 font-mono text-xs text-gray-600">{lic.deviceCode || lic.email || '—'}</td>
                  <td className="px-3 py-3 text-gray-600">{lic.customerName || '—'}</td>
                  {/* Version / Update — only products with an updater get controls.
                      `updateStatus` is absent entirely on e.g. FEA Desktop, so those
                      rows show em-dashes rather than a dropdown they cannot use. */}
                  <td className="px-3 py-3 text-xs text-gray-600">
                    {lic.updateStatus ? (
                      <>
                        {lic.currentVersion || <span className="text-gray-400">not seen yet</span>}
                        {/* The "last synced" the operator looks for. Deliberately NOT the
                            Last Check-in column: that one only moves on a successful
                            licence validation, this one also moves when the machine polls
                            for an update, so a machine whose licence is failing still
                            reads as alive. Sits under the version because it answers the
                            same question — is this what the machine is running NOW? */}
                        {lic.lastSeenAt && (
                          <span className="block text-[10px] text-gray-400 mt-0.5">
                            synced {new Date(lic.lastSeenAt).toLocaleString('en-IN')}
                          </span>
                        )}
                      </>
                    ) : '—'}
                  </td>
                  <td className="px-3 py-3">
                    {lic.updateStatus ? (
                      <div className="flex items-center gap-1.5" onClick={e => e.stopPropagation()}>
                        {/* The rollout routes are role:'admin' only, but this tab is NOT
                            admin-gated — a designer can read it. Show them the state,
                            never a control that would 403. */}
                        {isAdmin && (
                          <select value={lic.targetVersion || ''} disabled={targetSaving === lic.deviceCode}
                            onChange={e => saveTarget(lic.deviceCode, e.target.value || null)}
                            className="border rounded px-1.5 py-0.5 text-xs max-w-[7.5rem]">
                            <option value="">(default)</option>
                            {/* A pin that is not in the registered list (release deleted, or
                                the list failed to load) must still show as itself — otherwise
                                the select reads "(default)" while the pill says pending. */}
                            {[...new Set([...updateVersions, ...(lic.targetVersion ? [lic.targetVersion] : [])])]
                              .map(v => <option key={v} value={v}>{v}</option>)}
                          </select>
                        )}
                        {/* A machine we have never heard a version from is not "behind" —
                            calling it pending is a guess. Keyed on currentVersion, not on
                            whether a census row exists: pinning a target CREATES the row,
                            so seenByUpdater alone would then claim we had heard from it and
                            the cell would read "not seen yet … pending". */}
                        <span className={`px-2 py-0.5 rounded-full text-[10px] font-medium ${
                          !lic.currentVersion ? 'bg-gray-100 text-gray-500'
                            : (UPDATE_PILL[lic.updateStatus] || 'bg-gray-100 text-gray-500')}`}>
                          {!lic.currentVersion ? 'never seen' : lic.updateStatus}
                        </span>
                      </div>
                    ) : <span className="text-xs text-gray-400">—</span>}
                  </td>
                  <td className="px-3 py-3 text-center">
                    <span className={`inline-block px-2 py-0.5 rounded-full text-[11px] font-medium ${STATUS_PILL[lic.status] || 'bg-gray-100 text-gray-500'}`}>
                      {lic.status}
                    </span>
                  </td>
                  <td className="px-3 py-3 text-xs text-gray-500">{new Date(lic.createdAt).toLocaleDateString('en-IN')}</td>
                  <td className="px-3 py-3 text-xs text-gray-500">{lic.expiresAt ? new Date(lic.expiresAt).toLocaleDateString('en-IN') : '—'}</td>
                  <td className="px-3 py-3 text-xs text-gray-500">{lic.lastCheckedAt ? new Date(lic.lastCheckedAt).toLocaleString('en-IN') : 'Never'}</td>
                  <td className="px-3 py-3 text-right space-x-3" onClick={e => e.stopPropagation()}>
                    {isAdmin && lic.status === 'ACTIVE' && (
                      <button onClick={() => { setRevokeModal(lic.id); setRevokeReason('') }}
                        className="text-xs text-red-500 hover:text-red-700 font-medium">
                        Revoke
                      </button>
                    )}
                    {isAdmin && lic.status === 'REVOKED' && (
                      <button onClick={async () => {
                        try {
                          await updateLicense(lic.id, { status: 'ACTIVE' })
                          load(page, search)
                          getLicenseStats().then(r => setStats(r.data)).catch(() => {})
                        } catch {}
                      }}
                        className="text-xs text-green-600 hover:text-green-800 font-medium">
                        Reactivate
                      </button>
                    )}
                    {isAdmin && lic.status !== 'ACTIVE' && (
                      <button onClick={() => setDeleteModal({ id: lic.id, label: lic.deviceCode || lic.email || lic.id.slice(0,8) })}
                        className="text-xs text-gray-400 hover:text-red-600 font-medium">
                        Delete
                      </button>
                    )}
                  </td>
                </tr>

                {/* Expanded detail row */}
                {expandedId === lic.id && (
                  <tr>
                    <td colSpan={11} className="bg-gray-50 px-6 py-4">
                      {detailLoad ? (
                        <p className="text-sm text-gray-400">Loading details…</p>
                      ) : detail ? (
                        <div className="space-y-4">
                          {/* License info */}
                          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-xs">
                            <div><span className="text-gray-400">License ID:</span> <span className="font-mono text-gray-600">{detail.id.slice(0, 8)}…</span></div>
                            <div><span className="text-gray-400">Issued by:</span> <span className="text-gray-600">{detail.issuedBy}</span></div>
                            <div><span className="text-gray-400">Offline Grace:</span> <span className="text-gray-600">{detail.offlineGraceDays ?? (detail.type === 'PERMANENT' ? '30' : '7')} days</span></div>
                            {detail.notes && <div><span className="text-gray-400">Notes:</span> <span className="text-gray-600">{detail.notes}</span></div>}
                          </div>

                          {/* Expiry, editable after issue. The Issue form offers an end date only
                              for SUBSCRIPTION, but the backend expires whatever carries the field —
                              so a device-locked evaluation licence can be made to close itself. */}
                          <div className="flex flex-wrap items-end gap-2 text-xs">
                            <div>
                              <label className="block text-gray-400 mb-1">Expires</label>
                              <input type="date" value={expiryDraft} disabled={!isAdmin || expirySaving}
                                onChange={e => setExpiryDraft(e.target.value)}
                                className="input text-xs py-1" />
                            </div>
                            {isAdmin && (
                              <>
                                <button type="button" disabled={expirySaving || !expiryDraft}
                                  onClick={() => saveExpiry(detail.id, expiryDraft)}
                                  className="text-xs text-blue-600 hover:text-blue-800 font-medium disabled:text-gray-300 pb-1">
                                  {expirySaving ? 'Saving…' : 'Set expiry'}
                                </button>
                                {detail.expiresAt && (
                                  <button type="button" disabled={expirySaving}
                                    onClick={() => saveExpiry(detail.id, null)}
                                    className="text-xs text-gray-400 hover:text-gray-700 font-medium disabled:text-gray-300 pb-1">
                                    Clear
                                  </button>
                                )}
                              </>
                            )}
                            <span className="text-gray-400 pb-1">
                              {detail.expiresAt
                                ? `Runs out ${new Date(detail.expiresAt).toLocaleString('en-IN')}`
                                : 'No end date — this licence runs until it is revoked'}
                            </span>
                            {expiryErr && <span className="text-red-500 pb-1">{expiryErr}</span>}
                          </div>

                          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-xs">
                            {detail.revokedAt && (
                              <>
                                <div><span className="text-gray-400">Revoked at:</span> <span className="text-red-500">{new Date(detail.revokedAt).toLocaleString('en-IN')}</span></div>
                                <div><span className="text-gray-400">Revoked by:</span> <span className="text-gray-600">{detail.revokedBy}</span></div>
                                {detail.revokeReason && <div><span className="text-gray-400">Reason:</span> <span className="text-gray-600">{detail.revokeReason}</span></div>}
                              </>
                            )}
                          </div>

                          {/* Audit log */}
                          {detail.auditLog?.length > 0 && (
                            <div>
                              <p className="text-xs font-semibold text-gray-500 mb-2">Audit Log</p>
                              <div className="space-y-1">
                                {detail.auditLog.map(a => (
                                  <div key={a.id} className="flex items-center gap-3 text-xs text-gray-500">
                                    <span className="text-gray-400 w-32 shrink-0">{new Date(a.createdAt).toLocaleString('en-IN')}</span>
                                    <span className="font-medium text-gray-600">{a.action}</span>
                                    <span>{a.detail}</span>
                                    <span className="text-gray-400">by {a.performedBy}</span>
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}

                          {/* Licence check-in log. These are NOT activations — one row is
                              written every time the app launches and validates, so "42"
                              read as "activated 42 times" when it means "launched 42
                              times". The list is capped at 50 server-side, so its length
                              is not the total; show both, or this reads "50" for ever. */}
                          {detail.activations?.length > 0 && (
                            <div>
                              <p className="text-xs font-semibold text-gray-500 mb-2">
                                Licence Check-ins
                                <span className="ml-1.5 font-normal text-gray-400">
                                  {/* Never say "total" without _count. The list is capped
                                      server-side, so during a portal-ahead-of-backend deploy
                                      (or on a cached bundle spanning the two) this would read
                                      "50 total" — reinstating the exact defect the _count was
                                      added to remove. No total, no claim about a total. The cap
                                      itself is deliberately not duplicated here; a constant
                                      copied into the client is a constant that drifts. */}
                                  {detail._count?.activations == null
                                    ? `latest ${detail.activations.length}`
                                    : detail._count.activations > detail.activations.length
                                      ? `latest ${detail.activations.length} of ${detail._count.activations}`
                                      : `${detail.activations.length} total`}
                                </span>
                              </p>
                              <p className="text-[11px] text-gray-400 mb-2">
                                One entry per app launch — the licence check, not a re-activation.
                              </p>
                              <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
                                <table className="w-full text-xs">
                                  <thead>
                                    <tr className="bg-gray-50 text-gray-400 uppercase tracking-wide">
                                      <th className="px-3 py-2 text-left font-medium">Check-in</th>
                                      <th className="px-3 py-2 text-left font-medium">Device</th>
                                      <th className="px-3 py-2 text-left font-medium">App Version</th>
                                      <th className="px-3 py-2 text-left font-medium">OS</th>
                                      <th className="px-3 py-2 text-left font-medium">IP</th>
                                      <th className="px-3 py-2 text-center font-medium">Result</th>
                                    </tr>
                                  </thead>
                                  <tbody className="divide-y divide-gray-100">
                                    {detail.activations.map(act => (
                                      <tr key={act.id} className={act.success ? '' : 'bg-red-50'}>
                                        <td className="px-3 py-2 text-gray-500">{new Date(act.createdAt).toLocaleString('en-IN')}</td>
                                        <td className="px-3 py-2 font-mono text-gray-600">{act.deviceCode || '—'}</td>
                                        <td className="px-3 py-2 text-gray-600">{act.appVersion || '—'}</td>
                                        <td className="px-3 py-2 text-gray-600">{act.os || '—'}</td>
                                        <td className="px-3 py-2 text-gray-500">{act.ipAddress || '—'}</td>
                                        <td className="px-3 py-2 text-center">
                                          <span className={`inline-block px-2 py-0.5 rounded-full text-[10px] font-medium ${act.success ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-600'}`}>
                                            {act.success ? 'OK' : 'FAIL'}
                                          </span>
                                        </td>
                                      </tr>
                                    ))}
                                  </tbody>
                                </table>
                              </div>
                            </div>
                          )}
                        </div>
                      ) : (
                        <p className="text-sm text-red-400">Failed to load details</p>
                      )}
                    </td>
                  </tr>
                )}
              </Fragment>
            ))}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between text-sm text-gray-500">
          <span>{total} license{total !== 1 ? 's' : ''}</span>
          <div className="flex gap-2">
            <button onClick={() => { setPage(p => Math.max(1, p - 1)); load(page - 1, search) }}
              disabled={page <= 1} className="btn-secondary text-xs px-3 py-1.5 disabled:opacity-40">Prev</button>
            <span className="px-2 py-1.5 text-xs">Page {page} of {totalPages}</span>
            <button onClick={() => { setPage(p => Math.min(totalPages, p + 1)); load(page + 1, search) }}
              disabled={page >= totalPages} className="btn-secondary text-xs px-3 py-1.5 disabled:opacity-40">Next</button>
          </div>
        </div>
      )}

      {/* Revoke confirmation modal */}
      {revokeModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-sm mx-4 p-6">
            <h3 className="text-sm font-semibold text-gray-800 mb-3">Revoke License</h3>
            <p className="text-xs text-gray-500 mb-4">This will immediately invalidate the license. The user will lose access on their next app startup.</p>
            <div className="mb-4">
              <label className="block text-xs text-gray-500 mb-1">Reason (optional)</label>
              <input value={revokeReason} onChange={e => setRevokeReason(e.target.value)}
                placeholder="e.g. Subscription cancelled" className="input text-sm w-full" />
            </div>
            <div className="flex gap-2 justify-end">
              <button onClick={() => setRevokeModal(null)} className="btn-secondary text-sm px-4 py-2">Cancel</button>
              <button onClick={handleRevoke} disabled={revoking === revokeModal} className="btn-danger text-sm px-4 py-2">
                {revoking === revokeModal ? 'Revoking…' : 'Revoke'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete confirmation modal */}
      {deleteModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-sm mx-4 p-6">
            <h3 className="text-sm font-semibold text-gray-800 mb-3">Delete License</h3>
            <p className="text-xs text-gray-500 mb-2">
              Permanently delete <span className="font-mono font-medium text-gray-700">{deleteModal.label}</span>?
            </p>
            <p className="text-xs text-red-500 mb-4">This removes the record and all its activation history. Cannot be undone.</p>
            {deleteErr && <p className="text-xs text-red-600 bg-red-50 rounded px-3 py-2 mb-3">{deleteErr}</p>}
            <div className="flex gap-2 justify-end">
              <button onClick={() => { setDeleteModal(null); setDeleteErr(null) }} className="btn-secondary text-sm px-4 py-2">Cancel</button>
              <button onClick={handleDelete} disabled={deleting} className="btn-danger text-sm px-4 py-2">
                {deleting ? 'Deleting…' : 'Delete permanently'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Bulk purge confirmation modal */}
      {purgeModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-sm mx-4 p-6">
            <h3 className="text-sm font-semibold text-gray-800 mb-3">Bulk Purge</h3>
            <p className="text-xs text-gray-500 mb-2">
              Delete all <strong>{filterStat || 'REVOKED'}</strong> licenses
              {filterProd ? ` for product "${filterProd}"` : ' across all products'}?
            </p>
            <p className="text-xs text-red-500 mb-4">All matching records and their activation history will be permanently deleted.</p>
            {purgeErr && <p className="text-xs text-red-600 bg-red-50 rounded px-3 py-2 mb-3">{purgeErr}</p>}
            <div className="flex gap-2 justify-end">
              <button onClick={() => { setPurgeModal(false); setPurgeErr(null) }} className="btn-secondary text-sm px-4 py-2">Cancel</button>
              <button onClick={handleBulkPurge} disabled={purging} className="btn-danger text-sm px-4 py-2">
                {purging ? 'Purging…' : 'Purge all'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

// Verification-status pill colours for SurgConnect. Removed by mistake in
// dbbfcd71 (20 Jul 2026) along with the design-tools block it sat beside;
// the two uses below survived and threw at render until restored.
const SC_STATUS_COLORS = {
  PENDING:   'bg-yellow-100 text-yellow-800',
  APPROVED:  'bg-green-100 text-green-800',
  REJECTED:  'bg-red-100 text-red-800',
  SUSPENDED: 'bg-gray-100 text-gray-600',
}

function SurgConnectTab({ onPendingChange }) {
  const [doctors, setDoctors]         = useState([])
  const [loading, setLoading]         = useState(true)
  const [filter, setFilter]           = useState('PENDING')
  const [selected, setSelected]       = useState(null)
  const [rejectReason, setRejectReason] = useState('')
  const [acting, setActing]           = useState(false)

  useEffect(() => { load() }, [filter])

  function load() {
    setLoading(true)
    scGetDoctors({ status: filter || undefined })
      .then(r => setDoctors(r.data.doctors ?? r.data))
      .catch(() => setDoctors([]))
      .finally(() => setLoading(false))
  }

  async function act(id, action, reason) {
    const statusMap = { approve: 'APPROVED', reject: 'REJECTED', suspend: 'SUSPENDED' }
    setActing(true)
    try {
      await scVerifyDoctor(id, { status: statusMap[action], rejectionReason: reason || undefined })
      setSelected(null)
      setRejectReason('')
      load()
      scGetStats().then(r => onPendingChange?.(r.data.pendingCount ?? 0)).catch(() => {})
    } catch (e) {
      alert('Action failed: ' + (e.response?.data?.error || e.message))
    } finally {
      setActing(false)
    }
  }

  async function deleteDoctor(id, name) {
    if (!confirm(`Permanently delete Dr. ${name}? This cannot be undone.`)) return
    setActing(true)
    try {
      await scDeleteDoctor(id)
      setSelected(null)
      load()
      scGetStats().then(r => onPendingChange?.(r.data.pendingCount ?? 0)).catch(() => {})
    } catch (e) {
      alert('Delete failed: ' + (e.response?.data?.error || e.message))
    } finally {
      setActing(false)
    }
  }

  return (
    <div className="p-6 max-w-6xl mx-auto">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-lg font-semibold text-gray-800">SurgConnect — Doctor Registry</h2>
          <p className="text-sm text-gray-500 mt-0.5">Review and approve consultant surgeon registrations</p>
        </div>
        <select
          value={filter}
          onChange={e => setFilter(e.target.value)}
          className="border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500"
        >
          <option value="PENDING">Pending</option>
          <option value="APPROVED">Approved</option>
          <option value="REJECTED">Rejected</option>
          <option value="SUSPENDED">Suspended</option>
          <option value="">All</option>
        </select>
      </div>

      {loading ? (
        <p className="text-sm text-gray-400 text-center py-12">Loading…</p>
      ) : doctors.length === 0 ? (
        <p className="text-sm text-gray-400 text-center py-12">No doctors with status: {filter || 'any'}</p>
      ) : (
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="text-left px-4 py-3 font-medium text-gray-600">Name</th>
                <th className="text-left px-4 py-3 font-medium text-gray-600">Specialisation</th>
                <th className="text-left px-4 py-3 font-medium text-gray-600">City</th>
                <th className="text-left px-4 py-3 font-medium text-gray-600">Reg #</th>
                <th className="text-left px-4 py-3 font-medium text-gray-600">Fee (₹)</th>
                <th className="text-left px-4 py-3 font-medium text-gray-600">Status</th>
                <th className="text-left px-4 py-3 font-medium text-gray-600">Registered</th>
                <th className="px-4 py-3" />
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {doctors.map(d => (
                <tr key={d.id} className="hover:bg-gray-50">
                  <td className="px-4 py-3">
                    <p className="font-medium text-gray-800">{d.name}</p>
                    <p className="text-xs text-gray-400">{d.email}</p>
                  </td>
                  <td className="px-4 py-3 text-gray-700">{d.specialisation}</td>
                  <td className="px-4 py-3 text-gray-500">{[d.city, d.state].filter(Boolean).join(', ') || '—'}</td>
                  <td className="px-4 py-3 text-gray-500">{d.registrationNumber}</td>
                  <td className="px-4 py-3 text-gray-700">{d.consultationFeeInr ? `₹${Number(d.consultationFeeInr).toLocaleString('en-IN')}` : '—'}</td>
                  <td className="px-4 py-3">
                    <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${SC_STATUS_COLORS[d.verificationStatus] ?? 'bg-gray-100 text-gray-600'}`}>
                      {d.verificationStatus}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-gray-400 text-xs">{new Date(d.createdAt).toLocaleDateString('en-IN')}</td>
                  <td className="px-4 py-3">
                    <button
                      onClick={() => { setSelected(d); setRejectReason('') }}
                      className="text-xs text-brand-600 hover:underline font-medium"
                    >
                      Review
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Review slide-over / modal */}
      {selected && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-start justify-end" onClick={() => setSelected(null)}>
          <div
            className="bg-white w-full max-w-md h-full shadow-xl overflow-y-auto p-6 flex flex-col gap-5"
            onClick={e => e.stopPropagation()}
          >
            <div className="flex items-center justify-between">
              <h3 className="font-semibold text-gray-800 text-base">Doctor Profile</h3>
              <button onClick={() => setSelected(null)} className="text-gray-400 hover:text-gray-600 text-xl leading-none">×</button>
            </div>

            <div className="space-y-1">
              <p className="text-lg font-semibold text-gray-900">{selected.name}</p>
              <p className="text-sm text-gray-500">{selected.email}</p>
              {selected.phone && <p className="text-sm text-gray-500">{selected.phone}</p>}
            </div>

            <div className="grid grid-cols-2 gap-3 text-sm">
              <div><p className="text-xs text-gray-400 mb-0.5">Specialisation</p><p className="font-medium text-gray-800">{selected.specialisation}</p></div>
              <div><p className="text-xs text-gray-400 mb-0.5">Council</p><p className="font-medium text-gray-800">{selected.council}</p></div>
              <div><p className="text-xs text-gray-400 mb-0.5">Reg Number</p><p className="font-medium text-gray-800">{selected.registrationNumber}</p></div>
              <div><p className="text-xs text-gray-400 mb-0.5">Experience</p><p className="font-medium text-gray-800">{selected.yearsExperience ? `${selected.yearsExperience} yrs` : '—'}</p></div>
              <div><p className="text-xs text-gray-400 mb-0.5">Hospital</p><p className="font-medium text-gray-800">{selected.hospital || '—'}</p></div>
              <div><p className="text-xs text-gray-400 mb-0.5">City</p><p className="font-medium text-gray-800">{[selected.city, selected.state].filter(Boolean).join(', ') || '—'}</p></div>
              <div><p className="text-xs text-gray-400 mb-0.5">Consultation Fee</p><p className="font-medium text-gray-800">{selected.consultationFeeInr ? `₹${Number(selected.consultationFeeInr).toLocaleString('en-IN')}` : '—'}</p></div>
              <div><p className="text-xs text-gray-400 mb-0.5">Status</p><span className={`px-2 py-0.5 rounded-full text-xs font-medium ${SC_STATUS_COLORS[selected.verificationStatus]}`}>{selected.verificationStatus}</span></div>
            </div>

            {selected.bio && (
              <div><p className="text-xs text-gray-400 mb-1">Bio</p><p className="text-sm text-gray-700 leading-relaxed">{selected.bio}</p></div>
            )}
            {selected.subspecialities?.length > 0 && (
              <div>
                <p className="text-xs text-gray-400 mb-1">Subspecialities</p>
                <div className="flex flex-wrap gap-1.5">
                  {selected.subspecialities.map(s => <span key={s} className="bg-blue-50 text-blue-700 text-xs px-2 py-0.5 rounded-full">{s}</span>)}
                </div>
              </div>
            )}

            <div className="mt-auto pt-4 border-t border-gray-100 space-y-3">
              {selected.verificationStatus !== 'APPROVED' && (
                <button
                  disabled={acting}
                  onClick={() => act(selected.id, 'approve')}
                  className="w-full bg-green-600 text-white font-medium py-2.5 rounded-lg hover:bg-green-700 transition-colors disabled:opacity-60 text-sm"
                >
                  {acting ? 'Processing…' : 'Approve'}
                </button>
              )}
              {selected.verificationStatus !== 'REJECTED' && (
                <div className="space-y-2">
                  <input
                    value={rejectReason}
                    onChange={e => setRejectReason(e.target.value)}
                    placeholder="Rejection reason (optional)"
                    className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-red-400"
                  />
                  <button
                    disabled={acting}
                    onClick={() => act(selected.id, 'reject', rejectReason)}
                    className="w-full bg-red-500 text-white font-medium py-2.5 rounded-lg hover:bg-red-600 transition-colors disabled:opacity-60 text-sm"
                  >
                    {acting ? 'Processing…' : 'Reject'}
                  </button>
                </div>
              )}
              {selected.verificationStatus === 'APPROVED' && (
                <button
                  disabled={acting}
                  onClick={() => act(selected.id, 'suspend')}
                  className="w-full border border-gray-300 text-gray-600 font-medium py-2.5 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-60 text-sm"
                >
                  {acting ? 'Processing…' : 'Suspend'}
                </button>
              )}
              <button
                disabled={acting}
                onClick={() => deleteDoctor(selected.id, selected.name)}
                className="w-full border border-red-200 text-red-600 font-medium py-2 rounded-lg hover:bg-red-50 transition-colors disabled:opacity-60 text-xs"
              >
                Delete record
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

// ═════════════════════════════════════════════════════════════════
//  Forma Tab (multi-anatomy scan cases)
// ═════════════════════════════════════════════════════════════════
// Parallel to backend/src/config/scanTypes.js — keep in sync.
const FORMA_SCAN_TYPES = [
  { key: 'CRANIAL_HELMET', label: 'Cranial Helmet' },
  { key: 'FACE',           label: 'Front Face' },
  { key: 'EAR',            label: 'Ear' },
  { key: 'FEET',           label: 'Feet' },
  { key: 'ORTHOGNATHIC',   label: 'Orthognathic (facial surface)' },
  { key: 'BREAST',         label: 'Breast' },
  { key: 'OTHER',          label: 'Other' },
]

function FormaTab({ role }) {
  const isAdmin = role === 'admin'
  const [cases, setCases]       = useState([])
  const [loading, setLoading]   = useState(false)
  const [creating, setCreating] = useState(false)
  const [newCaseId, setNewCaseId] = useState('')
  const [newNotes, setNewNotes]   = useState('')
  const [newScanType, setNewScanType] = useState('CRANIAL_HELMET')
  const [newTypeLabel, setNewTypeLabel] = useState('')
  const [createErr, setCreateErr] = useState(null)
  const [busy, setBusy]           = useState(null)   // `${caseId}:${action}`
  const [copied, setCopied]       = useState(null)
  const [podiatryCase, setPodiatryCase] = useState(null)  // FEET case open in the orthotic order panel

  function load() {
    setLoading(true)
    getCranialCases()
      .then(res => setCases(res.data.cases || []))
      .catch(() => {})
      .finally(() => setLoading(false))
  }
  useEffect(() => { load() }, [])

  async function handleCreate() {
    setCreating(true); setCreateErr(null)
    try {
      await createCranialCase(
        newCaseId.trim() || undefined,
        newNotes.trim() || undefined,
        newScanType,
        newScanType === 'OTHER' ? newTypeLabel.trim() : undefined,
      )
      setNewCaseId(''); setNewNotes(''); setNewTypeLabel('')
      load()
    } catch (err) {
      setCreateErr(err.response?.data?.error || 'Failed to create case.')
    } finally { setCreating(false) }
  }

  async function run(caseId, action, fn) {
    setBusy(`${caseId}:${action}`)
    try { await fn(); load() }
    catch (err) { alert(err.response?.data?.error || 'Action failed.') }
    finally { setBusy(null) }
  }

  function copyPin(caseId, pin) {
    navigator.clipboard?.writeText(pin)
    setCopied(caseId); setTimeout(() => setCopied(null), 1500)
  }

  const STATUS = {
    AWAITING_SCAN: { label: 'Awaiting scan', cls: 'bg-gray-100 text-gray-500' },
    SCAN_RECEIVED: { label: 'Scan received', cls: 'bg-green-100 text-green-700' },
    MODEL_READY:   { label: 'Model ready', cls: 'bg-blue-100 text-blue-700' },
    REINITIATE:    { label: 'Re-scan requested', cls: 'bg-amber-100 text-amber-700' },
  }

  return (
    <div className="space-y-4">
      {/* Create */}
      <div className="bg-white rounded-xl border border-gray-200 p-5">
        <h3 className="text-sm font-semibold text-gray-700 mb-3">New Forma Scan Case</h3>
        {createErr && <p className="text-xs text-red-500 mb-2">{createErr}</p>}
        <div className="flex items-end gap-3 flex-wrap">
          <div className="w-52">
            <label className="text-xs text-gray-500 block mb-1">Scan type</label>
            <select
              value={newScanType}
              onChange={(e) => setNewScanType(e.target.value)}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-brand-300"
            >
              {FORMA_SCAN_TYPES.map(t => <option key={t.key} value={t.key}>{t.label}</option>)}
            </select>
          </div>
          {newScanType === 'OTHER' && (
            <div className="w-52">
              <label className="text-xs text-gray-500 block mb-1">Type label</label>
              <input
                type="text" value={newTypeLabel}
                onChange={(e) => setNewTypeLabel(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleCreate()}
                placeholder="e.g. Nasal splint"
                maxLength={40}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-300"
              />
            </div>
          )}
          <div className="w-52">
            <label className="text-xs text-gray-500 block mb-1">Case ID (optional)</label>
            <input
              type="text" value={newCaseId}
              onChange={(e) => setNewCaseId(e.target.value.toUpperCase().replace(/\s/g, ''))}
              onKeyDown={(e) => e.key === 'Enter' && handleCreate()}
              placeholder="Leave blank to auto-generate"
              maxLength={50}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-brand-300"
            />
          </div>
          <div className="flex-1 min-w-[160px]">
            <label className="text-xs text-gray-500 block mb-1">Notes (optional)</label>
            <input
              type="text" value={newNotes}
              onChange={(e) => setNewNotes(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleCreate()}
              placeholder="Patient name, side, …"
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-300"
            />
          </div>
          <button onClick={handleCreate} disabled={creating} className="btn-primary text-sm">
            {creating ? 'Creating…' : 'Create + PIN'}
          </button>
        </div>
        <p className="text-xs text-gray-400 mt-2">
          Creates a Case ID + 6-digit PIN. The scan type sets the auto-generated ID prefix (CRN/EAR/FEET/ORTHO) and the Drive folder. Give the PIN to the parent/doctor — the Forma app needs it (live) to scan and upload.
        </p>
      </div>

      {/* List */}
      <div className="bg-white rounded-xl border border-gray-200 overflow-x-auto">
        {loading ? (
          <div className="py-12 flex justify-center"><CentredSpinner /></div>
        ) : cases.length === 0 ? (
          <div className="py-12 text-center text-gray-400 text-sm">No Forma cases yet.</div>
        ) : (
          <table className="w-full text-sm min-w-[920px]">
            <thead>
              <tr className="border-b border-gray-100 text-[11px] text-gray-400 uppercase tracking-wide bg-gray-50">
                <th className="px-3 py-3 text-left font-medium">Case ID</th>
                <th className="px-3 py-3 text-center font-medium">Type</th>
                <th className="px-3 py-3 text-center font-medium">PIN</th>
                <th className="px-3 py-3 text-center font-medium">Status</th>
                <th className="px-3 py-3 text-center font-medium">Scans</th>
                <th className="px-3 py-3 text-left font-medium">Last upload</th>
                <th className="px-3 py-3 text-right font-medium">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {cases.map((c) => {
                const st = STATUS[c.status] || STATUS.AWAITING_SCAN
                return (
                  <tr key={c.caseId} className={`hover:bg-gray-50 transition-colors ${!c.isActive ? 'opacity-50' : ''}`}>
                    <td className="px-3 py-3 font-mono font-semibold text-gray-900">
                      {c.caseId}
                      {c.notes && <p className="text-[11px] text-gray-400 font-sans font-normal">{c.notes}</p>}
                    </td>
                    <td className="px-3 py-3 text-center">
                      <span className="text-xs font-medium px-2 py-0.5 rounded-full bg-brand-50 text-brand-700">
                        {c.scanTypeName || 'Cranial Helmet'}
                      </span>
                    </td>
                    <td className="px-3 py-3 text-center">
                      <button
                        onClick={() => copyPin(c.caseId, c.pin)}
                        className="font-mono text-lg font-bold text-brand-700 tracking-wider hover:text-brand-500"
                        title="Click to copy"
                      >
                        {c.pin || '—'}
                      </button>
                      <div className="text-[10px] text-gray-400">{copied === c.caseId ? 'Copied!' : 'tap to copy'}</div>
                    </td>
                    <td className="px-3 py-3 text-center">
                      <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${st.cls}`}>{st.label}</span>
                    </td>
                    <td className="px-3 py-3 text-center">{c.scanCount || 0}</td>
                    <td className="px-3 py-3 text-gray-500 text-xs">
                      {c.lastUpload ? new Date(c.lastUpload).toLocaleString() : '—'}
                    </td>
                    <td className="px-3 py-3 whitespace-nowrap">
                      <div className="flex items-center justify-end gap-1.5">
                        {c.scanType === 'FEET' && (
                          <button
                            onClick={() => setPodiatryCase(c.caseId)}
                            className="text-xs px-2 py-1 rounded-lg border border-brand-200 bg-brand-50 text-brand-700 hover:bg-brand-100 font-medium"
                            title="Open the orthotic order form (intake, measurements, PDF)"
                          >
                            Podiatry
                          </button>
                        )}
                        {isAdmin && (
                          <button
                            onClick={() => run(c.caseId, 'pin', () => regenerateCranialPin(c.caseId))}
                            disabled={busy === `${c.caseId}:pin`}
                            className="text-[11px] text-gray-400 hover:text-brand-600 disabled:opacity-50"
                          >
                            {busy === `${c.caseId}:pin` ? '…' : 'Regenerate PIN'}
                          </button>
                        )}
                        <button
                          onClick={() => run(c.caseId, 'reinit', () => reinitiateCranialCase(c.caseId))}
                          disabled={busy === `${c.caseId}:reinit`}
                          className="text-xs px-2 py-1 rounded-lg border border-amber-200 bg-amber-50 text-amber-700 hover:bg-amber-100 font-medium disabled:opacity-50"
                          title="Flag this case so the parent re-scans"
                        >
                          {busy === `${c.caseId}:reinit` ? '…' : 'Re-initiate'}
                        </button>
                        {isAdmin && (
                          <button
                            onClick={() => run(c.caseId, 'active', () => setCranialCaseActive(c.caseId, !c.isActive))}
                            disabled={busy === `${c.caseId}:active`}
                            className="text-xs px-2 py-1 rounded-lg border border-gray-200 text-gray-500 hover:bg-gray-50 font-medium disabled:opacity-50"
                          >
                            {c.isActive ? 'Deactivate' : 'Activate'}
                          </button>
                        )}
                        {isAdmin && !c.isActive && (
                          <button
                            onClick={() => { if (window.confirm(`Delete ${c.caseId} and its scan records?\n\nGoogle Drive files are NOT removed — clean those up manually. This cannot be undone.`)) run(c.caseId, 'del', () => deleteCranialCase(c.caseId)) }}
                            disabled={busy === `${c.caseId}:del`}
                            className="text-xs px-2 py-1 rounded-lg border border-red-200 bg-red-50 text-red-700 hover:bg-red-100 font-medium disabled:opacity-50"
                            title="Permanently delete this case (Drive files remain)"
                          >
                            {busy === `${c.caseId}:del` ? '…' : 'Delete'}
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        )}
      </div>

      {podiatryCase && (
        <PodiatryFormPanel caseId={podiatryCase} onClose={() => { setPodiatryCase(null); load() }} />
      )}
    </div>
  )
}
